/**
 * Zones Screen — A2-S1 (list view) + A2-S2 (creation wizard)
 *
 * List view: zone table with live occupancy bars from useDensityStream,
 * filter bar (type / status / risk), tab strip (My Zones / All / Critical /
 * Archived), and per-row archive action (system_admin only).
 *
 * Creation wizard: 5-step modal overlay (preset → draw → configure alerts →
 * assign → review). Drawing uses Leaflet click events (no leaflet-draw
 * dependency). Wizard state in a single useReducer.
 *
 * POST /api/v1/crowd/zones on confirm. The Crowd API facade creates the zone
 * and persists wizard alert rules through the Crowd & Event Operations Pack.
 *
 * No PII — zone creation takes operational metadata only.
 */

import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, ZoomControl, Polygon, Polyline, Circle, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import type { Alert, AlertRule as SharedAlertRule, AlertSeverity, CrowdAlertType, DensitySnapshot, Operation, Zone, ZoneType, RiskLevel, GeoJSONGeometry, Venue, VenueType } from '@vigilo/shared';
import { useApp } from '@/shell/AppContext';
import {
  type ColorPalette, Icons, KLabel, StatusDot, Badge, Pill, Panel, Btn, Tabs, StatusColor, SeverityColor,
  FONT_SIZE, FONT_WEIGHT, LETTER_SPACING, LINE_HEIGHT, RADIUS, SPACE, SIZE,
} from '@vigilo/design-system';
import { getCrowdC } from '../../design/crowdPalette';
import { get, patch, post } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { useAlertData, useDensityData } from '../data/useMockData';
import { MOCK_ALERTS, MOCK_HISTORY, MOCK_ZONES, ZONE_ID } from '../data/mock';
import { useVenues, type CreateVenueDto } from '../hooks/useVenues';
import {
  CROWD_CLIENT_ACTIVITY_ACTION,
  CROWD_CLIENT_RESOURCE_TYPE,
  useCrowdActivityAudit,
  useCrowdFilterActivity,
} from '../hooks/useCrowdActivityAudit';
import { useCrowdTaxonomy, type CrowdTaxonomy, type CrowdZonePreset } from '../hooks/useCrowdTaxonomy';
import { useMapProviderConfig, type MapBasemapId, type MapGeocodingConfig } from '../hooks/useMapProviderConfig';
import { isZoneMetricFresh, useDensityHistory, useLiveZoneMetrics, zoneMetricsDensitySnapshots, type CurrentZoneMetrics, type DensityHistoryPoint } from '../hooks/useLiveZoneMetrics';
import { useTeams, type CrowdTeam } from '../hooks/useTeams';
import { useZoneAlertSummaries } from '../hooks/useZoneAlertSummaries';
import { PaginationControls } from '../components/PaginationControls';
import { resolveZoneStatus, zoneStatusColor, zoneStatusLabel, alertCountColor, trendColor } from '../utils/zone-status';
import { useLocalPagination } from '../hooks/useLocalPagination';
import { crowdQuery } from '../apiScope';

const BASEMAP_LABELS: Record<MapBasemapId, string> = {
  dark: 'Dark',
  satellite: 'Sat',
  light: 'Light',
};
const UNASSIGNED_TEAM_VALUE = '__unassigned__';
type ZoneActivationMode = 'live' | 'scheduled' | 'manual';

interface CrowdZoneActivationMetadata {
  mode?: ZoneActivationMode;
  status?: 'active' | 'scheduled' | 'inactive';
  scheduledStart?: string;
  scheduledEnd?: string;
}

function crowdZoneActivation(zone: Pick<Zone, 'metadata'>): Required<Pick<CrowdZoneActivationMetadata, 'mode' | 'status'>> & CrowdZoneActivationMetadata {
  const metadata = zone.metadata?.crowdActivation as CrowdZoneActivationMetadata | undefined;
  if (metadata?.mode === 'scheduled') return { ...metadata, mode: 'scheduled', status: 'scheduled' };
  // A manual zone's status comes from metadata; default to 'inactive' only when missing.
  if (metadata?.mode === 'manual') return { ...metadata, mode: 'manual', status: metadata.status ?? 'inactive' };
  return { ...metadata, mode: 'live', status: 'active' };
}

function zoneActivationLabel(zone: Pick<Zone, 'metadata'>): string {
  const activation = crowdZoneActivation(zone);
  if (activation.mode === 'scheduled') return 'Scheduled';
  if (activation.mode === 'manual') return 'Manual';
  return 'Live';
}

function zoneActivationDetail(zone: Pick<Zone, 'metadata' | 'activatedAt'>): string {
  const activation = crowdZoneActivation(zone);
  if (activation.mode === 'scheduled') {
    const start = activation.scheduledStart ? formatDateTime(activation.scheduledStart) : 'Schedule pending';
    const end = activation.scheduledEnd ? formatDateTime(activation.scheduledEnd) : null;
    return end ? `${start} - ${end}` : start;
  }
  if (activation.mode === 'manual') return activation.status === 'active' ? 'Active' : 'Inactive until activated';
  return `Activated ${formatDateTime(zone.activatedAt)}`;
}

function zoneActivationColor(zone: Pick<Zone, 'metadata'>, C: ColorPalette): string {
  const activation = crowdZoneActivation(zone);
  if (activation.mode === 'scheduled') return C.teal;
  if (activation.mode === 'manual') return activation.status === 'active' ? C.green : C.muted;
  return C.green;
}

function localDateTimeToIso(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function activeCrowdTeams(teams: CrowdTeam[]): CrowdTeam[] {
  return teams.filter(team => team.status === 'active' && team.archivedAt === null);
}

function buildTeamById(teams: CrowdTeam[]): Map<string, CrowdTeam> {
  return new Map(teams.map(team => [team.id, team]));
}

function operationTeamIdForZone(operation: Operation | null | undefined, zoneId: string): string | null {
  if (!operation) return null;
  const assignment = operation.zoneTeamAssignments.find(item => item.zoneId === zoneId);
  if (!assignment) return operation.defaultTeamId ?? null;
  return assignment.inheritedFromDefault ? operation.defaultTeamId ?? null : assignment.teamId ?? null;
}

function operationTeamNameForZone(
  operation: Operation | null | undefined,
  zoneId: string,
  teamById: Map<string, CrowdTeam>,
  fallback: string | null,
): string | null {
  const teamId = operationTeamIdForZone(operation, zoneId);
  if (!teamId) return fallback;
  return teamById.get(teamId)?.name ?? fallback;
}

function activeAlertCountForZoneInMode(
  alerts: Alert[],
  zoneId: string,
  operation: Operation | null | undefined,
  isCatalogMode: boolean,
): number {
  return alerts.filter(alert => {
    if (alert.zoneId !== zoneId || alert.status === 'resolved') return false;
    if (isCatalogMode) return true;
    return Boolean(operation && alert.operationId === operation.id && operation.assignedZoneIds.includes(alert.zoneId));
  }).length;
}

// ── Alert rule ────────────────────────────────────────────────────────────────

interface AlertRule {
  id:            string;
  alertType:     string;
  label:         string;
  enabled:       boolean;
  condition:     string;
  threshold:     number;
  unit:          string;
  windowMinutes: number;
  priority:      AlertSeverity;
  autoEscalateAfterMin?: number;
  tokenOnly?:    boolean;
}

const SUSTAINED_CLUSTER_ALERT_TYPE = 'sustained_loitering_cluster';
const DEFAULT_SUSTAINED_CLUSTER_PERSON_COUNT = 10;
const CATALOG_CROWD_ALERT_TYPES = [
  'overcrowding',
  'crowd_surge',
  'sudden_dispersal',
  'restricted_zone_gathering',
  'sustained_loitering_cluster',
  'threshold_breach',
  'zone_capacity_warning',
  'flagged_token_entry',
  'flagged_token_reentry',
] as const satisfies readonly CrowdAlertType[];
const CATALOG_CROWD_ALERT_TYPE_SET = new Set<string>(CATALOG_CROWD_ALERT_TYPES);
const LEGACY_CATALOG_ALERT_TYPE_MAP: Record<string, CrowdAlertType> = {
  dwell_time_exceeded: SUSTAINED_CLUSTER_ALERT_TYPE,
  flow_congestion: 'threshold_breach',
};

interface CreateCrowdZoneAlertRulePayload {
  alertType: CrowdAlertType;
  enabled: boolean;
  thresholds: {
    densityPct?: number;
    surgeRatePct?: number;
    timeWindowMin?: number;
    personCount?: number;
    dwellMinutes?: number;
    reentryWindowHours?: number;
  };
  priority: AlertSeverity;
  autoEscalateAfterMin?: number;
}

type ZoneDetailTab = 'overview' | 'activity' | 'triggers';
type ZoneEventFilter = 'all' | 'density' | 'alerts' | 'zone_changes' | 'team_actions';
type ZoneEventRange = '1h' | '6h' | '24h' | 'custom';
type ZoneScreenMode = 'operational' | 'catalog';

interface ZoneActivityEvent {
  id:          string;
  category:    Exclude<ZoneEventFilter, 'all'>;
  eventType:   string;
  description: string;
  detail?:     string;
  timestamp:   string;
  colorKey:    string;
  icon:        keyof typeof Icons;
}

interface ZoneAlertRule {
  id:                  string;
  zoneId:              string;
  alertType:           CrowdAlertType;
  enabled:             boolean;
  thresholds: {
    densityPct?:         number;
    surgeRatePct?:       number;
    timeWindowMin?:      number;
    personCount?:        number;
    dwellMinutes?:       number;
    reentryWindowHours?: number;
  };
  priority:            AlertSeverity;
  autoEscalateAfterMin?: number;
  lastTriggeredAt:     string | null;
}

// ── Preset definitions ────────────────────────────────────────────────────────

interface PresetDef {
  zoneType: ZoneType;
  label: string;
  description: string;
  icon: (props: { size?: number; sw?: number }) => JSX.Element;
  defaultThresholds: Record<string, {
    densityPct?: number;
    surgeRatePct?: number;
    timeWindowMin?: number;
    personCount?: number;
    dwellMinutes?: number;
    reentryWindowHours?: number;
    severity: AlertSeverity;
    autoEscalateAfterMin?: number;
  }>;
}

function presetIcon(icon: string | null): PresetDef['icon'] {
  if (icon === 'users') return Icons.Users;
  if (icon === 'shield-alert') return Icons.Shield;
  if (icon === 'move-right' || icon === 'log-in') return Icons.ArrowR;
  if (icon === 'map-pin') return Icons.Alert;
  if (icon === 'scan-line') return Icons.Crosshair;
  if (icon === 'clock') return Icons.Settings;
  return Icons.Grid;
}

function ruleLabel(alertType: string): string {
  return alertType
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function ruleFromThreshold(
  alertType: string,
  threshold: PresetDef['defaultThresholds'][string],
): Omit<AlertRule, 'id'> {
  const value = threshold.densityPct
    ?? threshold.surgeRatePct
    ?? threshold.personCount
    ?? threshold.reentryWindowHours
    ?? 0;
  const unit = threshold.densityPct != null || threshold.surgeRatePct != null
    ? '%'
    : threshold.personCount != null
      ? 'persons'
      : threshold.reentryWindowHours != null
        ? 'hours'
        : 'event';
  const windowMinutes = threshold.timeWindowMin ?? threshold.dwellMinutes ?? 0;
  return {
    alertType,
    label: ruleLabel(alertType),
    enabled: true,
    condition: value > 0 ? `${ruleLabel(alertType)} threshold` : `${ruleLabel(alertType)} event`,
    threshold: value,
    unit,
    windowMinutes,
    priority: threshold.severity,
    autoEscalateAfterMin: threshold.autoEscalateAfterMin,
    tokenOnly: alertType.startsWith('flagged_token_'),
  };
}

function presetRules(preset: PresetDef): Omit<AlertRule, 'id'>[] {
  return Object.entries(preset.defaultThresholds).map(([alertType, threshold]) => (
    ruleFromThreshold(alertType, threshold)
  ));
}

function normalizeCatalogAlertType(alertType: string | undefined): CrowdAlertType | null {
  if (!alertType) return null;
  const normalized = LEGACY_CATALOG_ALERT_TYPE_MAP[alertType] ?? alertType;
  return CATALOG_CROWD_ALERT_TYPE_SET.has(normalized) ? normalized as CrowdAlertType : null;
}

interface PresetView extends PresetDef {
  type: ZoneType;
  name: string;
  desc: string;
  rules: Omit<AlertRule, 'id'>[];
}

interface PersistedPresetRule {
  alertType?: string;
  thresholds?: {
    densityPct?: number;
    surgePctPerMin?: number;
    surgeRatePct?: number;
    timeWindowMin?: number;
    personCount?: number;
    dwellMinutes?: number;
    reentryWindowHours?: number;
  };
  priority?: AlertSeverity;
  autoEscalateAfterMin?: number;
  enabled?: boolean;
}

function ruleFromPresetRule(rule: PersistedPresetRule): Omit<AlertRule, 'id'> | null {
  const alertType = normalizeCatalogAlertType(rule.alertType);
  if (!alertType) return null;
  const thresholds = rule.thresholds ?? {};
  const personCount = alertType === SUSTAINED_CLUSTER_ALERT_TYPE && thresholds.personCount == null && thresholds.dwellMinutes != null
    ? DEFAULT_SUSTAINED_CLUSTER_PERSON_COUNT
    : thresholds.personCount;
  const threshold = thresholds.densityPct
    ?? thresholds.surgePctPerMin
    ?? thresholds.surgeRatePct
    ?? personCount
    ?? thresholds.reentryWindowHours
    ?? 0;
  const unit = thresholds.densityPct != null || thresholds.surgePctPerMin != null || thresholds.surgeRatePct != null
    ? '%'
    : personCount != null
      ? 'persons'
      : thresholds.reentryWindowHours != null
        ? 'hours'
        : 'event';
  return {
    alertType,
    label: ruleLabel(alertType),
    enabled: rule.enabled ?? true,
    condition: threshold > 0 ? `${ruleLabel(alertType)} threshold` : `${ruleLabel(alertType)} event`,
    threshold,
    unit,
    windowMinutes: thresholds.timeWindowMin ?? thresholds.dwellMinutes ?? 0,
    priority: rule.priority ?? 'medium',
    autoEscalateAfterMin: rule.autoEscalateAfterMin,
    tokenOnly: alertType.startsWith('flagged_token_'),
  };
}

function presetFallbackRules(preset: CrowdZonePreset): Omit<AlertRule, 'id'>[] {
  const thresholds: PresetDef['defaultThresholds'] = {};
  if (typeof preset.defaultThresholds.overcrowding === 'number') {
    thresholds.overcrowding = { densityPct: preset.defaultThresholds.overcrowding, severity: 'high' };
  }
  if (typeof preset.defaultThresholds.capacityWarning === 'number') {
    thresholds.zone_capacity_warning = { densityPct: preset.defaultThresholds.capacityWarning, severity: 'medium' };
  }
  if (typeof preset.defaultThresholds.surge === 'number') {
    thresholds.crowd_surge = { surgeRatePct: preset.defaultThresholds.surge, timeWindowMin: 5, severity: 'high' };
  }
  return presetRules({
    zoneType: preset.zoneType,
    label: preset.label,
    description: preset.description ?? '',
    icon: presetIcon(preset.icon),
    defaultThresholds: thresholds,
  });
}

function buildPresetDefs(taxonomy: CrowdTaxonomy | null): PresetView[] {
  if (!taxonomy) return [];
  const enabledZoneTypes = new Set(taxonomy.zoneTypes.map(item => item.code));
  return taxonomy.zonePresets
    .filter(preset => enabledZoneTypes.has(preset.zoneType))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(preset => {
      const persistedRules = preset.rules
        .map(rule => ruleFromPresetRule(rule as PersistedPresetRule))
        .filter((rule): rule is Omit<AlertRule, 'id'> => Boolean(rule));
      return {
        zoneType: preset.zoneType,
        label: preset.label,
        description: preset.description ?? '',
        icon: presetIcon(preset.icon),
        defaultThresholds: {},
        type: preset.zoneType,
        name: preset.label,
        desc: preset.description ?? '',
        rules: persistedRules.length > 0 ? persistedRules : presetFallbackRules(preset),
      };
    });
}

function wizardRuleThresholds(rule: AlertRule): CreateCrowdZoneAlertRulePayload['thresholds'] {
  const thresholds: CreateCrowdZoneAlertRulePayload['thresholds'] = {};
  if (rule.unit === '%') {
    if (rule.alertType === 'crowd_surge' || rule.alertType === 'sudden_dispersal') {
      thresholds.surgeRatePct = rule.threshold;
    } else {
      thresholds.densityPct = rule.threshold;
    }
  } else if (rule.unit === 'persons') {
    thresholds.personCount = rule.threshold;
  } else if (rule.unit === 'hours') {
    thresholds.reentryWindowHours = rule.threshold;
  }

  if (rule.windowMinutes > 0) {
    if (rule.alertType === 'sustained_loitering_cluster') {
      thresholds.dwellMinutes = rule.windowMinutes;
    } else {
      thresholds.timeWindowMin = rule.windowMinutes;
    }
  }
  return thresholds;
}

function wizardRulePayload(rule: AlertRule): CreateCrowdZoneAlertRulePayload {
  return {
    alertType: rule.alertType as CrowdAlertType,
    enabled: rule.enabled,
    thresholds: wizardRuleThresholds(rule),
    priority: rule.priority,
    autoEscalateAfterMin: rule.autoEscalateAfterMin,
  };
}

// ── Wizard state ──────────────────────────────────────────────────────────────

interface WizardState {
  step:         number;
  venueId:      string | null;
  venueLocked:  boolean;
  temporary:    boolean;
  preset:       ZoneType | null;
  drawMode:     'polygon' | 'rectangle' | 'circle' | 'corridor';
  drawVertices: number[][];
  circleRadiusM: number;
  geometry:     GeoJSONGeometry | null;
  alertRules:   AlertRule[];
  name:         string;
  description:  string;
  capacity:     number;
  riskLevel:    RiskLevel;
  assignedTeamId: string;
  activation:   ZoneActivationMode;
  activationScheduledStart: string;
  activationScheduledEnd: string;
}

type WizardAction =
  | { type: 'GO_TO_STEP';    step: number }
  | { type: 'INIT_CONTEXT';  venueId: string | null; venueLocked: boolean; temporary: boolean; step: number; assignedTeamId?: string }
  | { type: 'SET_VENUE';     venueId: string | null }
  | { type: 'SET_TEMPORARY'; temporary: boolean }
  | { type: 'SET_PRESET';    preset: ZoneType; presetDef: PresetView }
  | { type: 'SET_DRAW_MODE'; mode: 'polygon' | 'rectangle' | 'circle' | 'corridor' }
  | { type: 'ADD_VERTEX';    vertex: [number, number] }
  | { type: 'SET_DRAW_VERTICES'; vertices: number[][] }
  | { type: 'SET_CIRCLE_RADIUS'; radiusM: number; geometry?: GeoJSONGeometry | null }
  | { type: 'SET_DRAW_GEOMETRY'; geometry: GeoJSONGeometry; vertices?: number[][] }
  | { type: 'COMPLETE_POLYGON' }
  | { type: 'RESET_DRAW' }
  | { type: 'TOGGLE_RULE';   ruleId: string }
  | { type: 'UPDATE_RULE';   ruleId: string; key: 'threshold' | 'windowMinutes'; value: number }
  | { type: 'SET_FIELD';     field: keyof Pick<WizardState, 'name' | 'description' | 'capacity' | 'riskLevel' | 'assignedTeamId' | 'activation' | 'activationScheduledStart' | 'activationScheduledEnd'>; value: string | number }
  | { type: 'RESET' };

const INITIAL_STATE: WizardState = {
  step: 0, venueId: null, venueLocked: false, temporary: false,
  preset: null, drawMode: 'polygon',
  drawVertices: [], circleRadiusM: 50, geometry: null, alertRules: [],
  name: '', description: '', capacity: 1000,
  riskLevel: 'medium', assignedTeamId: '', activation: 'live',
  activationScheduledStart: '', activationScheduledEnd: '',
};

let ruleSeq = 0;
function mkRuleId() { return `r${++ruleSeq}`; }

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'GO_TO_STEP':
      return { ...state, step: action.step };

    case 'INIT_CONTEXT':
      return {
        ...INITIAL_STATE,
        venueId:     action.venueId,
        venueLocked: action.venueLocked,
        temporary:   action.temporary,
        step:        action.step,
        assignedTeamId: action.assignedTeamId ?? '',
      };

    case 'SET_VENUE':
      return { ...state, venueId: action.venueId };

    case 'SET_TEMPORARY':
      return { ...state, temporary: action.temporary };

    case 'SET_PRESET': {
      const def = action.presetDef;
      return {
        ...state,
        preset:     action.preset,
        alertRules: def.rules.map(r => ({ ...r, id: mkRuleId() })),
        name:       def ? `${def.name} — New` : '',
      };
    }

    case 'SET_DRAW_MODE':
      return { ...state, drawMode: action.mode, drawVertices: [], geometry: null };

    case 'ADD_VERTEX':
      return { ...state, drawVertices: [...state.drawVertices, action.vertex] };

    case 'SET_DRAW_VERTICES':
      return { ...state, drawVertices: action.vertices };

    case 'SET_CIRCLE_RADIUS':
      return { ...state, circleRadiusM: action.radiusM, geometry: action.geometry === undefined ? state.geometry : action.geometry };

    case 'SET_DRAW_GEOMETRY':
      return {
        ...state,
        geometry: action.geometry,
        drawVertices: action.vertices ?? state.drawVertices,
      };

    case 'COMPLETE_POLYGON': {
      let verts = state.drawVertices;
      if (verts.length < 3) return state;
      // Remove the duplicate vertex that Leaflet's click+dblclick combination adds.
      if (verts.length > 3) {
        const a = verts[verts.length - 1], b = verts[verts.length - 2];
        if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.0001) {
          verts = verts.slice(0, -1);
        }
      }
      const ring = [...verts, verts[0]];
      return { ...state, geometry: { type: 'Polygon', coordinates: [ring] } };
    }

    case 'RESET_DRAW':
      return { ...state, drawVertices: [], geometry: null };

    case 'TOGGLE_RULE':
      return {
        ...state,
        alertRules: state.alertRules.map(r => r.id === action.ruleId ? { ...r, enabled: !r.enabled } : r),
      };

    case 'UPDATE_RULE':
      return {
        ...state,
        alertRules: state.alertRules.map(r => r.id === action.ruleId ? { ...r, [action.key]: action.value } : r),
      };

    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };

    case 'RESET':
      return { ...INITIAL_STATE };

    default:
      return state;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function riskColor(risk: string, C: ColorPalette): string {
  if (risk === 'critical') return C.ember;
  if (risk === 'high')     return C.amber;
  if (risk === 'medium')   return C.sub;
  return C.muted;
}

function formatRelTime(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)   return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function zoneMetricFreshnessText(metric?: CurrentZoneMetrics | null): string {
  if (!metric) return 'No projection';
  const label = metric.freshnessStatus === 'fresh' || (!metric.freshnessStatus && isZoneMetricFresh(metric))
    ? 'Live'
    : 'Stale';
  return `${label} ${formatRelTime(metric.lastEventAt)}`;
}

function zoneMetricFreshnessColor(metric: CurrentZoneMetrics | null | undefined, C: ColorPalette): string {
  if (!metric) return C.muted;
  return isZoneMetricFresh(metric) ? C.green : C.amber;
}

function formatRuleType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\bre\b/i, 'Re-').replace(/\b\w/g, c => c.toUpperCase());
}

/** Utilisation display: '—' when there is no snapshot; Number() coerces string decimals from the API. */
function formatUtilPct(value: unknown, hasSnapshot: boolean): string {
  if (!hasSnapshot) return '—';
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(n < 1 ? 2 : 1)}%`;
}

function densityStatus(util: number): 'normal' | 'warning' | 'alert' {
  if (util >= 85) return 'alert';
  if (util >= 70) return 'warning';
  return 'normal';
}

function trendIcon(direction?: string) {
  if (direction === 'rising') return <Icons.ArrowU size={SIZE.iconSm} />;
  if (direction === 'falling') return <Icons.ArrowD size={SIZE.iconSm} />;
  return <Icons.ArrowR size={SIZE.iconSm} />;
}

function thresholdDescription(rule: ZoneAlertRule): string {
  if (rule.alertType === 'flagged_token_entry') {
    return 'Fires when an analyst-flagged anonymous token enters this zone. No subscriber identity involved.';
  }
  if (rule.alertType === 'flagged_token_reentry') {
    return 'Fires when an analyst-flagged anonymous token re-enters this zone. No subscriber identity involved.';
  }
  if (rule.alertType === 'crowd_surge') {
    return `Fires when crowd density increases by ${rule.thresholds.surgeRatePct ?? 20}% within ${rule.thresholds.timeWindowMin ?? 5} min`;
  }
  if (rule.alertType === 'restricted_zone_gathering') {
    return `Fires immediately when person count >= ${rule.thresholds.personCount ?? 1} in this restricted zone`;
  }
  if (rule.alertType === 'zone_capacity_warning') {
    return `Fires when occupancy exceeds ${rule.thresholds.densityPct ?? 70}% of capacity`;
  }
  if (rule.alertType === 'threshold_breach') {
    return `Fires when restricted corridor density exceeds ${rule.thresholds.densityPct ?? 60}%`;
  }
  return `Fires when occupancy exceeds ${rule.thresholds.densityPct ?? 85}% of capacity`;
}

function primaryThreshold(rule: ZoneAlertRule): number | null {
  if (rule.alertType === 'flagged_token_entry' || rule.alertType === 'flagged_token_reentry') return null;
  return rule.thresholds.densityPct ?? rule.thresholds.surgeRatePct ?? rule.thresholds.personCount ?? null;
}

function setPrimaryThreshold(rule: ZoneAlertRule, value: number): ZoneAlertRule {
  if (rule.thresholds.densityPct != null) return { ...rule, thresholds: { ...rule.thresholds, densityPct: value } };
  if (rule.thresholds.surgeRatePct != null) return { ...rule, thresholds: { ...rule.thresholds, surgeRatePct: value } };
  return { ...rule, thresholds: { ...rule.thresholds, personCount: value } };
}

function buildMockZoneEvents(zone: Zone, zoneTypeLabel: (type: ZoneType) => string): ZoneActivityEvent[] {
  const history = MOCK_HISTORY[zone.id] ?? [];
  const events: ZoneActivityEvent[] = [
    {
      id: `${zone.id}-activated`,
      category: 'zone_changes',
      eventType: 'zone_activated',
      description: 'Zone activated',
      detail: `Monitoring started for ${zoneTypeLabel(zone.type)}`,
      timestamp: zone.activatedAt,
      colorKey: 'warning',
      icon: 'Check',
    },
    {
      id: `${zone.id}-assigned`,
      category: 'team_actions',
      eventType: 'team_assigned',
      description: 'Operation team assignment updated',
      detail: 'Operational ownership updated',
      timestamp: zone.updatedAt,
      colorKey: 'muted',
      icon: 'Users',
    },
    {
      id: `${zone.id}-edited`,
      category: 'zone_changes',
      eventType: 'zone_edited',
      description: `Zone capacity updated to ${zone.capacity.toLocaleString()}`,
      detail: 'Configuration changed by operations',
      timestamp: new Date(new Date(zone.updatedAt).getTime() - 10 * 60_000).toISOString(),
      colorKey: 'warning',
      icon: 'Edit',
    },
  ];

  for (let i = 1; i < history.length; i += 1) {
    const prev = history[i - 1];
    const curr = history[i];
    if (prev.capacityUtilisation < 85 && curr.capacityUtilisation >= 85) {
      events.push({
        id: `${zone.id}-density-${i}`,
        category: 'density',
        eventType: 'density_threshold_crossed',
        description: 'Density crossed 85% threshold',
        detail: `Density rose from ${prev.capacityUtilisation}% to ${curr.capacityUtilisation}%`,
        timestamp: curr.timestamp,
        colorKey: 'teal',
        icon: 'Activity',
      });
    }
  }

  MOCK_ALERTS.filter(a => a.zoneId === zone.id).forEach(alert => {
    events.push({
      id: `${alert.id}-created`,
      category: 'alerts',
      eventType: 'alert_created',
      description: `${formatRuleType(alert.alertType)} alert created`,
      detail: alert.triggerContext.condition,
      timestamp: alert.triggeredAt,
      colorKey: alert.severity,
      icon: 'Alert',
    });
    if (alert.acknowledgedAt) {
      events.push({
        id: `${alert.id}-ack`,
        category: 'alerts',
        eventType: 'alert_acknowledged',
        description: 'Alert acknowledged',
        detail: `Alert ${alert.id} moved to acknowledged`,
        timestamp: alert.acknowledgedAt,
        colorKey: 'teal',
        icon: 'Check',
      });
    }
    if (alert.resolvedAt) {
      events.push({
        id: `${alert.id}-resolved`,
        category: 'alerts',
        eventType: 'alert_resolved',
        description: `Alert resolved — ${alert.resolutionNote ?? 'No note recorded'}`,
        detail: 'Resolution saved to alert lifecycle',
        timestamp: alert.resolvedAt,
        colorKey: 'normal',
        icon: 'Check',
      });
    }
  });

  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/**
 * Real events derived from real data sources only (density history + operation alerts).
 * Zone config/team-assignment change history has no real per-zone audit query available,
 * so those categories are empty in live mode rather than fabricated.
 */
function buildRealZoneEvents(zone: Zone, alerts: Alert[], densityHistoryPoints: DensityHistoryPoint[]): ZoneActivityEvent[] {
  const events: ZoneActivityEvent[] = [];
  const sortedHistory = densityHistoryPoints
    .slice()
    .sort((a, b) => new Date(a.sampledAt).getTime() - new Date(b.sampledAt).getTime());
  for (let i = 1; i < sortedHistory.length; i += 1) {
    const prev = sortedHistory[i - 1];
    const curr = sortedHistory[i];
    if (Number(prev.capacityUtilisation) < 85 && Number(curr.capacityUtilisation) >= 85) {
      events.push({
        id: `${zone.id}-density-${curr.sampledAt}`,
        category: 'density',
        eventType: 'density_threshold_crossed',
        description: 'Density crossed 85% threshold',
        detail: `Density rose from ${Number(prev.capacityUtilisation).toFixed(1)}% to ${Number(curr.capacityUtilisation).toFixed(1)}%`,
        timestamp: curr.sampledAt,
        colorKey: 'teal',
        icon: 'Activity',
      });
    }
  }
  for (const alert of alerts.filter(a => a.zoneId === zone.id)) {
    events.push({
      id: `${alert.id}-created`,
      category: 'alerts',
      eventType: 'alert_created',
      description: `${formatRuleType(alert.alertType)} alert created`,
      detail: alert.triggerContext?.condition,
      timestamp: alert.triggeredAt,
      colorKey: alert.severity,
      icon: 'Alert',
    });
    if (alert.acknowledgedAt) {
      events.push({
        id: `${alert.id}-ack`,
        category: 'alerts',
        eventType: 'alert_acknowledged',
        description: 'Alert acknowledged',
        detail: `Alert ${alert.id} moved to acknowledged`,
        timestamp: alert.acknowledgedAt,
        colorKey: 'teal',
        icon: 'Check',
      });
    }
    if (alert.resolvedAt) {
      events.push({
        id: `${alert.id}-resolved`,
        category: 'alerts',
        eventType: 'alert_resolved',
        description: `Alert resolved${alert.resolutionNote ? ` — ${alert.resolutionNote}` : ''}`,
        detail: 'Resolution saved to alert lifecycle',
        timestamp: alert.resolvedAt,
        colorKey: 'normal',
        icon: 'Check',
      });
    }
  }
  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function buildMockRules(zone: Zone): ZoneAlertRule[] {
  const lastAlert = (type: CrowdAlertType) =>
    MOCK_ALERTS.find(a => a.zoneId === zone.id && a.alertType === type)?.triggeredAt ?? null;

  const rules: ZoneAlertRule[] = [
    {
      id: `${zone.id}-overcrowding`,
      zoneId: zone.id,
      alertType: 'overcrowding',
      enabled: true,
      thresholds: { densityPct: 85, timeWindowMin: 2 },
      priority: 'critical',
      autoEscalateAfterMin: 5,
      lastTriggeredAt: lastAlert('overcrowding'),
    },
    {
      id: `${zone.id}-capacity-warning`,
      zoneId: zone.id,
      alertType: 'zone_capacity_warning',
      enabled: true,
      thresholds: { densityPct: 70, timeWindowMin: 1 },
      priority: 'medium',
      lastTriggeredAt: lastAlert('zone_capacity_warning'),
    },
    {
      id: `${zone.id}-crowd-surge`,
      zoneId: zone.id,
      alertType: 'crowd_surge',
      enabled: true,
      thresholds: { surgeRatePct: 20, timeWindowMin: 5 },
      priority: 'high',
      autoEscalateAfterMin: 10,
      lastTriggeredAt: lastAlert('crowd_surge'),
    },
  ];

  if (zone.id === ZONE_ID.EEC || zone.id === ZONE_ID.VIP) {
    rules.push(
      {
        id: `${zone.id}-threshold-breach`,
        zoneId: zone.id,
        alertType: 'threshold_breach',
        enabled: true,
        thresholds: { densityPct: 60, timeWindowMin: 1 },
        priority: 'critical',
        autoEscalateAfterMin: 3,
        lastTriggeredAt: lastAlert('threshold_breach'),
      },
      {
        id: `${zone.id}-flag-entry`,
        zoneId: zone.id,
        alertType: 'flagged_token_entry',
        enabled: true,
        thresholds: {},
        priority: 'high',
        lastTriggeredAt: lastAlert('flagged_token_entry'),
      },
      {
        id: `${zone.id}-flag-reentry`,
        zoneId: zone.id,
        alertType: 'flagged_token_reentry',
        enabled: true,
        thresholds: { reentryWindowHours: 2 },
        priority: 'high',
        lastTriggeredAt: lastAlert('flagged_token_reentry'),
      },
    );
  }

  return rules;
}

function zoneAlertRuleFromPersisted(rule: SharedAlertRule): ZoneAlertRule | null {
  const alertType = normalizeCatalogAlertType(rule.alertType);
  if (!alertType) return null;
  return {
    id: rule.id,
    zoneId: rule.zoneId,
    alertType,
    enabled: rule.enabled,
    thresholds: rule.thresholds,
    priority: rule.priority,
    autoEscalateAfterMin: rule.autoEscalateAfterMin,
    lastTriggeredAt: null,
  };
}

function latestTriggeredAtForRule(
  alerts: Alert[],
  zoneId: string,
  alertType: CrowdAlertType,
): string | null {
  return alerts
    .filter(alert => alert.zoneId === zoneId && alert.alertType === alertType)
    .map(alert => alert.triggeredAt)
    .filter(Boolean)
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0] ?? null;
}

function rulesWithLiveTriggerTimes(
  rules: ZoneAlertRule[],
  alerts: Alert[],
  zoneId: string,
): ZoneAlertRule[] {
  return rules.map(rule => ({
    ...rule,
    lastTriggeredAt: latestTriggeredAtForRule(alerts, zoneId, rule.alertType) ?? rule.lastTriggeredAt,
  }));
}

function zoneCode(zone: Pick<Zone, 'name' | 'type'>, zoneTypeCode: (type: ZoneType) => string): string {
  const code = zoneTypeCode(zone.type);
  return code === 'UNK'
    ? zone.name.split(/\s+/).filter(Boolean).map(w => w[0]?.toUpperCase() ?? '').join('').slice(0, 5)
    : code;
}

function allowsOperationalZoneActions(operation: { status: string } | null | undefined): boolean {
  return operation?.status === 'active' || operation?.status === 'paused';
}

// Display a shortened zone UUID (e.g. "#a1b2c3d")
function shortId(id: string): string {
  return id.length > 10 ? `#${id.slice(0, 7)}` : id;
}

function metersBetween(a: number[], b: number[]): number {
  const lat = ((a[1] + b[1]) / 2) * Math.PI / 180;
  const dx = (b[0] - a[0]) * 111_320 * Math.cos(lat);
  const dy = (b[1] - a[1]) * 111_320;
  return Math.hypot(dx, dy);
}

function circleGeometry(center: number[], radiusM: number, segments = 72): GeoJSONGeometry {
  const [lng, lat] = center;
  const latRad = lat * Math.PI / 180;
  const latDeg = radiusM / 111_320;
  const lngDeg = radiusM / (111_320 * Math.max(Math.cos(latRad), 0.0001));
  const ring: number[][] = [];
  for (let i = 0; i < segments; i += 1) {
    const t = (i / segments) * Math.PI * 2;
    ring.push([lng + Math.cos(t) * lngDeg, lat + Math.sin(t) * latDeg]);
  }
  ring.push(ring[0]);
  return { type: 'Polygon', coordinates: [ring] };
}

function rectangleGeometry(a: number[], b: number[]): GeoJSONGeometry {
  const ring = [
    [a[0], a[1]],
    [b[0], a[1]],
    [b[0], b[1]],
    [a[0], b[1]],
    [a[0], a[1]],
  ];
  return { type: 'Polygon', coordinates: [ring] };
}

function corridorGeometry(points: number[][], widthM = 18): GeoJSONGeometry | null {
  if (points.length < 2) return null;
  const left: number[][] = [];
  const right: number[][] = [];
  const half = widthM / 2;

  for (let i = 0; i < points.length; i += 1) {
    const prev = points[Math.max(0, i - 1)];
    const curr = points[i];
    const next = points[Math.min(points.length - 1, i + 1)];
    const lat = curr[1] * Math.PI / 180;
    const vxM = (next[0] - prev[0]) * 111_320 * Math.cos(lat);
    const vyM = (next[1] - prev[1]) * 111_320;
    const len = Math.hypot(vxM, vyM) || 1;
    const nxM = -vyM / len;
    const nyM = vxM / len;
    const lngOffset = (nxM * half) / (111_320 * Math.max(Math.cos(lat), 0.0001));
    const latOffset = (nyM * half) / 111_320;
    left.push([curr[0] + lngOffset, curr[1] + latOffset]);
    right.unshift([curr[0] - lngOffset, curr[1] - latOffset]);
  }

  return { type: 'Polygon', coordinates: [[...left, ...right, left[0]]] };
}

// ── Map drawing controller ────────────────────────────────────────────────────

function DrawingController({ dispatch, drawMode, vertices, circleRadiusM, isCompleted }: {
  dispatch: React.Dispatch<WizardAction>;
  drawMode: WizardState['drawMode'];
  vertices: number[][];
  circleRadiusM: number;
  isCompleted: boolean;
}) {
  const lastPos  = useRef<[number, number] | null>(null);
  const lastTime = useRef(0);
  const circleCenterRef = useRef<[number, number] | null>(null);
  const circleDraggingRef = useRef(false);

  useMapEvents({
    click: (e) => {
      if (isCompleted) return;
      if (drawMode === 'circle') {
        const center: [number, number] = [e.latlng.lng, e.latlng.lat];
        dispatch({
          type: 'SET_DRAW_GEOMETRY',
          vertices: [center],
          geometry: circleGeometry(center, circleRadiusM),
        });
        return;
      }
      if (drawMode === 'rectangle') {
        const pos: [number, number] = [e.latlng.lng, e.latlng.lat];
        if (vertices.length === 0) {
          dispatch({ type: 'SET_DRAW_VERTICES', vertices: [pos] });
        } else {
          dispatch({
            type: 'SET_DRAW_GEOMETRY',
            vertices: [vertices[0], pos],
            geometry: rectangleGeometry(vertices[0], pos),
          });
        }
        return;
      }
      if (drawMode === 'corridor') {
        dispatch({ type: 'ADD_VERTEX', vertex: [e.latlng.lng, e.latlng.lat] });
        return;
      }
      const now = Date.now();
      const pos: [number, number] = [e.latlng.lng, e.latlng.lat];
      // Skip rapid-fire clicks at the same position (double-click side effect).
      if (lastPos.current && now - lastTime.current < 300) {
        const d = Math.hypot(pos[0] - lastPos.current[0], pos[1] - lastPos.current[1]);
        if (d < 0.0001) return;
      }
      lastPos.current  = pos;
      lastTime.current = now;
      dispatch({ type: 'ADD_VERTEX', vertex: pos });
    },
    dblclick: () => {
      if (isCompleted) return;
      if (drawMode === 'polygon' && vertices.length >= 3) {
        dispatch({ type: 'COMPLETE_POLYGON' });
      }
      if (drawMode === 'corridor' && vertices.length >= 2) {
        const geometry = corridorGeometry(vertices);
        if (geometry) dispatch({ type: 'SET_DRAW_GEOMETRY', geometry });
      }
    },
    mousedown: (e) => {
      if (isCompleted || drawMode !== 'circle') return;
      const center: [number, number] = [e.latlng.lng, e.latlng.lat];
      circleCenterRef.current = center;
      circleDraggingRef.current = true;
      dispatch({ type: 'SET_DRAW_VERTICES', vertices: [center] });
    },
    mousemove: (e) => {
      if (isCompleted || drawMode !== 'circle' || !circleDraggingRef.current || !circleCenterRef.current) return;
      const edge: [number, number] = [e.latlng.lng, e.latlng.lat];
      const radiusM = Math.max(1, Math.round(metersBetween(circleCenterRef.current, edge)));
      dispatch({ type: 'SET_CIRCLE_RADIUS', radiusM, geometry: circleGeometry(circleCenterRef.current, radiusM) });
      dispatch({ type: 'SET_DRAW_VERTICES', vertices: [circleCenterRef.current, edge] });
    },
    mouseup: (e) => {
      if (drawMode !== 'circle' || !circleDraggingRef.current || !circleCenterRef.current) return;
      const edge: [number, number] = [e.latlng.lng, e.latlng.lat];
      const radiusM = Math.max(1, Math.round(metersBetween(circleCenterRef.current, edge)));
      dispatch({ type: 'SET_CIRCLE_RADIUS', radiusM, geometry: circleGeometry(circleCenterRef.current, radiusM) });
      dispatch({ type: 'SET_DRAW_VERTICES', vertices: [circleCenterRef.current, edge] });
      circleDraggingRef.current = false;
      circleCenterRef.current = null;
    },
  });
  return null;
}

// ── Geometry thumbnail (SVG) for Step 5 review ────────────────────────────────

interface LocationSearchResult {
  id: string;
  label: string;
  lat: number;
  lng: number;
}

interface ApiResponse<T> {
  data: T;
}

const COORDINATE_RE = /^\s*(?:lat(?:itude)?\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(?:(?:lng|lon|longitude)\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*$/i;

function parseLatLngSearch(value: string): LocationSearchResult | null {
  const match = value.trim().match(COORDINATE_RE);
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return {
    id: 'coordinates',
    label: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    lat,
    lng,
  };
}

function MapLocationSearch({ C, geocoding }: { C: ColorPalette; geocoding: MapGeocodingConfig | null }) {
  const map = useMap();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastMovedCoordinate = useRef<string | null>(null);

  function moveTo(result: LocationSearchResult) {
    map.flyTo([result.lat, result.lng], Math.max(map.getZoom(), 17), { duration: 0.7 });
    setQuery(result.label);
    setResults([]);
    setError(null);
  }

  useEffect(() => {
    const value = query.trim();
    if (!value) {
      setResults([]);
      setError(null);
      setLoading(false);
      lastMovedCoordinate.current = null;
      return;
    }

    const controller = new AbortController();
    let active = true;
    const request = window.setTimeout(() => {
      const coordinateResult = parseLatLngSearch(value);
      if (coordinateResult) {
        if (lastMovedCoordinate.current !== coordinateResult.label) {
          moveTo(coordinateResult);
          lastMovedCoordinate.current = coordinateResult.label;
        }
        return;
      }
      lastMovedCoordinate.current = null;

      if (!geocoding) {
        setError('Location search is unavailable.');
        setResults([]);
        setLoading(false);
        return;
      }
      if (value.length < geocoding.minimumQueryLength) {
        setResults([]);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        applicationId: 'crowd_monitoring',
        limit: String(geocoding.maxResults),
        q: value,
      });

      get<ApiResponse<LocationSearchResult[]>>(`${geocoding.searchEndpoint}?${params.toString()}`)
        .then(response => {
          if (!active || controller.signal.aborted) return;
          const data = Array.isArray(response.data) ? response.data : [];
          const next = data.filter(item =>
            item
            && typeof item.id === 'string'
            && typeof item.label === 'string'
            && Number.isFinite(item.lat)
            && Number.isFinite(item.lng)
          );

          setResults(next);
          setError(next.length === 0 ? 'No matching location found.' : null);
        })
        .catch(err => {
          if (!active || (err as Error).name === 'AbortError') return;
          setError('Location search is unavailable.');
          setResults([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(request);
      controller.abort();
    };
  }, [geocoding, query]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(event?: React.FormEvent) {
    event?.preventDefault();
    const coordinateResult = parseLatLngSearch(query);
    if (coordinateResult) moveTo(coordinateResult);
    else if (results[0]) moveTo(results[0]);
  }

  return (
    <div
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      style={{
        position: 'absolute', top: 12, left: 12, zIndex: 500,
        width: 380, maxWidth: 'calc(100% - 96px)',
        fontFamily: "'Albert Sans', sans-serif",
      }}
    >
      <form onSubmit={handleSubmit} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: `${C.surface}f2`, border: `1px solid ${C.border2}`,
        borderRadius: 5, padding: 6, boxShadow: '0 8px 18px rgba(0,0,0,0.28)',
      }}>
        <span style={{ color: C.muted, display: 'inline-flex', alignItems: 'center', paddingLeft: 4 }}>
          <Icons.Search size={13} />
        </span>
        <input
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setError(null);
          }}
          placeholder="Search place or lat, lon"
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none',
            background: 'transparent', color: C.text, fontSize: 12,
            fontFamily: "'Albert Sans', sans-serif",
          }}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setError(null); }}
            style={{
              width: 24, height: 24, border: 'none', borderRadius: 4,
              background: 'transparent', color: C.muted, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
            title="Clear search"
          >
            <Icons.X size={12} />
          </button>
        )}
        {loading && (
          <span style={{ color: C.amber, fontSize: 10.5, fontWeight: 700, padding: '0 6px', whiteSpace: 'nowrap' }}>
            Searching
          </span>
        )}
      </form>

      {(results.length > 0 || error) && (
        <div style={{
          marginTop: 6, background: `${C.surface}f7`, border: `1px solid ${C.border2}`,
          borderRadius: 5, overflow: 'hidden', boxShadow: '0 8px 18px rgba(0,0,0,0.28)',
        }}>
          {error && (
            <div style={{ padding: '9px 11px', color: C.ember, fontSize: 11.5 }}>
              {error}
            </div>
          )}
          {results.map(result => (
            <button
              key={result.id}
              type="button"
              onClick={() => moveTo(result)}
              style={{
                width: '100%', textAlign: 'left', border: 'none',
                borderBottom: `1px solid ${C.border}`,
                background: 'transparent', color: C.text, cursor: 'pointer',
                padding: '8px 10px', fontFamily: "'Albert Sans', sans-serif",
              }}
            >
              <div style={{
                fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {result.label}
              </div>
              <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2, fontFamily: 'monospace' }}>
                {result.lat.toFixed(5)}, {result.lng.toFixed(5)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GeometryThumbnail({ geometry, C }: { geometry: GeoJSONGeometry; C: ColorPalette }) {
  if (geometry.type !== 'Polygon') return null;
  const ring = (geometry.coordinates as number[][][])[0];
  const lngs = ring.map(([lng]) => lng);
  const lats = ring.map(([, lat]) => lat);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const W = 260, H = 120, pad = 14;

  function toSvgPoint([lng, lat]: number[]): string {
    const x = pad + ((lng - minLng) / ((maxLng - minLng) || 1)) * (W - 2 * pad);
    const y = H - pad - ((lat - minLat) / ((maxLat - minLat) || 1)) * (H - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, background: C.surface, borderRadius: 4, border: `1px solid ${C.border}` }}>
      <polygon points={ring.map(toSvgPoint).join(' ')} fill={`${C.amber}25`} stroke={C.amber} strokeWidth={1.5} />
    </svg>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ enabled, onClick, C, disabled = false }: { enabled: boolean; onClick: () => void; C: ColorPalette; disabled?: boolean }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        width: 34, height: 18, borderRadius: 9,
        background: enabled ? C.amber : C.faint,
        border: `1px solid ${enabled ? C.amber : C.border3}`,
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', flexShrink: 0,
        opacity: disabled ? 0.55 : 1,
        transition: 'all 150ms',
      }}
    >
      <span style={{
        position: 'absolute', top: 1, left: enabled ? 16 : 1,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        transition: 'left 150ms',
      }} />
    </div>
  );
}

// ── Threshold input ───────────────────────────────────────────────────────────

function ThresholdInput({ label, value, unit, onChange, min, max, step = 1, C }: {
  label: string; value: number; unit: string;
  onChange: (v: number) => void;
  min: number; max: number; step?: number;
  C: ColorPalette;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 4px 4px 10px',
    }}>
      <span style={{ fontSize: 10.5, color: C.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
      <input
        type="number" value={value} min={min} max={max} step={step}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: 58, padding: '5px 6px', fontSize: 12, fontWeight: 700,
          color: C.text, background: C.panel, border: `1px solid ${C.border2}`,
          borderRadius: 3, textAlign: 'center', fontFamily: 'monospace', outline: 'none',
        }}
      />
      <span style={{ fontSize: 10.5, color: C.muted, paddingRight: 6 }}>{unit}</span>
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────

function FormField({ label, children, C }: { label: string; children: React.ReactNode; C: ColorPalette }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: C.muted, marginBottom: 6,
      }}>{label}</div>
      {children}
    </div>
  );
}

const inputCss = (C: ColorPalette): React.CSSProperties => ({
  width: '100%', padding: '9px 12px', fontSize: 12.5,
  background: C.surface, color: C.text, border: `1px solid ${C.border2}`,
  borderRadius: 4, fontFamily: "'Albert Sans', sans-serif", outline: 'none', boxSizing: 'border-box',
});

function venueLabel(venue: Venue | undefined): string {
  return venue ? `${venue.name} — ${venue.city}` : 'Unknown venue';
}

// ── Wizard: step progress bar ─────────────────────────────────────────────────

const WIZARD_STEPS = [
  { n: 0, label: 'Venue'    },
  { n: 1, label: 'Preset'   },
  { n: 2, label: 'Draw'     },
  { n: 3, label: 'Configure'},
  { n: 4, label: 'Assign'   },
  { n: 5, label: 'Review'   },
];

function WizardStepper({ step, C }: { step: number; C: ColorPalette }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', maxWidth: 800, margin: '0 auto' }}>
      {WIZARD_STEPS.map((s, i) => {
        const isDone   = step > s.n;
        const isActive = step === s.n;
        return (
          <Fragment key={s.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: isDone ? C.green : isActive ? C.amber : C.faint,
                border: `1.5px solid ${isDone ? C.green : isActive ? C.amber : C.border3}`,
                color: (isDone || isActive) ? '#fff' : C.muted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>
                {isDone ? <Icons.Check size={13} /> : s.n}
              </div>
              <div>
                <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted }}>
                  Step {s.n}
                </div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: isActive ? C.text : C.sub }}>
                  {s.label}
                </div>
              </div>
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1.5, background: step > s.n ? C.green : C.border2, margin: '0 14px' }} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Step 0 — Select Venue ─────────────────────────────────────────────────────

function CreateVenueInlineModal({ C, onClose, onCreate }: {
  C:        ColorPalette;
  onClose:  () => void;
  onCreate: (dto: CreateVenueDto) => Promise<void>;
}) {
  const { taxonomy, loading: taxonomyLoading } = useCrowdTaxonomy();
  const venueTypes = taxonomy?.venueTypes ?? [];
  const [name, setName] = useState('');
  const [venueType, setVenueType] = useState<VenueType | ''>('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [capacity, setCapacity] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit() {
    if (!venueType) {
      setErr('Venue type is required.');
      return;
    }
    if (!name.trim() || !city.trim() || !country.trim() || !capacity.trim()) {
      setErr('Name, city, country and capacity are required.');
      return;
    }
    const totalCapacity = Number(capacity);
    if (!Number.isFinite(totalCapacity) || totalCapacity < 1) {
      setErr('Capacity must be a positive number.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onCreate({
        name: name.trim(),
        venueType,
        city: city.trim(),
        country: country.trim().toUpperCase().slice(0, 2),
        address: address.trim() || undefined,
        totalCapacity,
      });
      onClose();
    } catch {
      setErr('Failed to create venue — check Core API.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border2}`, borderRadius: RADIUS.panel,
        width: SIZE.reportModal, maxWidth: '90vw', fontFamily: "'Albert Sans', sans-serif",
      }}>
        <div style={{
          padding: `${SPACE[16]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: FONT_SIZE.heading, fontWeight: FONT_WEIGHT.bold, color: C.text }}>Create Venue</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer' }}>
            <Icons.X size={SIZE.iconSm} />
          </button>
        </div>
        <div style={{ padding: SPACE[18], display: 'flex', flexDirection: 'column', gap: SPACE[12] }}>
          <FormField label="Venue name *" C={C}>
            <input value={name} onChange={e => setName(e.target.value)} style={inputCss(C)} placeholder="e.g. Johan Cruijff ArenA" autoFocus />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[12] }}>
            <FormField label="Venue type *" C={C}>
              <select
                value={venueType}
                onChange={e => setVenueType(e.target.value as VenueType)}
                style={inputCss(C)}
                disabled={taxonomyLoading || venueTypes.length === 0}
              >
                <option value="">{taxonomyLoading ? 'Loading venue types...' : 'Select venue type'}</option>
                {venueTypes.map(type => (
                  <option key={type.code} value={type.code}>{type.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Capacity *" C={C}>
              <input type="number" min={1} value={capacity} onChange={e => setCapacity(e.target.value)} style={inputCss(C)} placeholder="54990" />
            </FormField>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE[12] }}>
            <FormField label="City *" C={C}>
              <input value={city} onChange={e => setCity(e.target.value)} style={inputCss(C)} placeholder="Amsterdam" />
            </FormField>
            <FormField label="Country *" C={C}>
              <input value={country} onChange={e => setCountry(e.target.value)} maxLength={2} style={inputCss(C)} placeholder="NL" />
            </FormField>
          </div>
          <FormField label="Address" C={C}>
            <input value={address} onChange={e => setAddress(e.target.value)} style={inputCss(C)} placeholder="Street address" />
          </FormField>
          {err && (
            <div style={{ fontSize: FONT_SIZE.body, color: C.ember, background: C.emberDim, border: `1px solid ${C.ember}55`, borderRadius: RADIUS.sm, padding: SPACE[10] }}>
              {err}
            </div>
          )}
        </div>
        <div style={{ padding: `${SPACE[12]}px ${SPACE[18]}px`, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: SPACE[8] }}>
          <Btn C={C} variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn C={C} variant="primary" disabled={saving} icon={<Icons.Plus size={SIZE.iconSm} />} onClick={handleSubmit}>
            {saving ? 'Creating…' : 'Create Venue'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function TemporaryZoneToggle({ temporary, onChange, C }: {
  temporary: boolean;
  onChange: (temporary: boolean) => void;
  C: ColorPalette;
}) {
  return (
    <Panel C={C} style={{ padding: SPACE[16] }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE[14] }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[3] }}>
            {temporary ? 'Operation-specific zone' : 'Permanent venue zone'}
          </div>
          <div style={{ fontSize: FONT_SIZE.xs, color: temporary ? C.amber : C.muted, lineHeight: LINE_HEIGHT.relaxed }}>
            {temporary
              ? 'This zone will be archived when the operation closes.'
              : 'This zone will remain at the venue for future operations.'}
          </div>
        </div>
        <Toggle enabled={temporary} onClick={() => onChange(!temporary)} C={C} />
      </div>
    </Panel>
  );
}

function Step0Venue({ wiz, venues, loading, createVenue, dispatch, C }: {
  wiz: WizardState;
  venues: Venue[];
  loading: boolean;
  createVenue: (dto: CreateVenueDto) => Promise<Venue>;
  dispatch: React.Dispatch<WizardAction>;
  C: ColorPalette;
}) {
  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const activeVenues = venues.filter(venue => !venue.archivedAt);
  const selectedVenue = venues.find(venue => venue.id === wiz.venueId);

  async function handleCreateVenue(dto: CreateVenueDto) {
    const venue = await createVenue(dto);
    dispatch({ type: 'SET_VENUE', venueId: venue.id });
  }

  return (
    <div>
      <div style={{ fontSize: FONT_SIZE.stepTitle, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[4] }}>
        Select Venue
      </div>
      <div style={{ fontSize: FONT_SIZE.body, color: C.muted, marginBottom: SPACE[20] }}>
        Choose the venue this zone belongs to
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: SPACE[18] }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[12] }}>
          <FormField label="Venue *" C={C}>
            {wiz.venueLocked ? (
              <div>
                <div style={{
                  ...inputCss(C), display: 'flex', alignItems: 'center', gap: SPACE[8],
                  color: C.muted, cursor: 'default',
                }}>
                  <Icons.Lock size={SIZE.iconSm} />
                  <span>{venueLabel(selectedVenue)}</span>
                </div>
                <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[6] }}>
                  Venue set from {wiz.temporary ? 'selected Operation' : 'Venue Management'}
                </div>
              </div>
            ) : (
              <select
                value={wiz.venueId ?? ''}
                onChange={e => {
                  if (e.target.value === '__create__') {
                    setShowCreateVenue(true);
                    return;
                  }
                  dispatch({ type: 'SET_VENUE', venueId: e.target.value || null });
                }}
                style={inputCss(C)}
                disabled={loading}
              >
                <option value="">{loading ? 'Loading venues…' : 'Select a venue...'}</option>
                {activeVenues.map(venue => (
                  <option key={venue.id} value={venue.id}>{venueLabel(venue)}</option>
                ))}
                <option value="__create__">+ Create new venue</option>
              </select>
            )}
          </FormField>

          {!wiz.venueLocked && activeVenues.length === 0 && !loading && (
            <div style={{
              background: C.panel, border: `1px dashed ${C.border3}`, borderRadius: RADIUS.lg,
              padding: SPACE[14], fontSize: FONT_SIZE.body, color: C.muted,
            }}>
              No active venues found. Create a venue before continuing.
            </div>
          )}
        </div>

        <TemporaryZoneToggle
          temporary={wiz.temporary}
          onChange={temporary => dispatch({ type: 'SET_TEMPORARY', temporary })}
          C={C}
        />
      </div>

      {showCreateVenue && (
        <CreateVenueInlineModal
          C={C}
          onClose={() => setShowCreateVenue(false)}
          onCreate={handleCreateVenue}
        />
      )}
    </div>
  );
}

// ── Step 1 — Select Preset ────────────────────────────────────────────────────

function Step1Preset({ wiz, presetDefs, dispatch, C }: {
  wiz: WizardState;
  presetDefs: PresetView[];
  dispatch: React.Dispatch<WizardAction>;
  C: ColorPalette;
}) {
  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Choose a zone type preset</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
        Each preset includes default alert thresholds for the zone's operational role. Adjust them in Step 3.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
      {presetDefs.map(p => {
        const selected = wiz.preset === p.type;
        const Ico = p.icon;
          return (
            <button
              key={p.type}
              data-testid={`zone-preset-${p.type}`}
              onClick={() => dispatch({ type: 'SET_PRESET', preset: p.type, presetDef: p })}
              style={{
                textAlign: 'left', padding: 14, borderRadius: 6,
                background: selected ? C.amberDim : C.panel,
                border: `1.5px solid ${selected ? C.amber : C.border2}`,
                cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
                transition: 'all 120ms',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 6, flexShrink: 0,
                  background: selected ? C.amber : C.faint,
                  color: selected ? '#fff' : C.amber,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Ico size={16} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</span>
                {selected && <span style={{ marginLeft: 'auto', color: C.amber }}><Icons.Check size={16} /></span>}
              </div>
              <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.5 }}>{p.desc}</div>
              {p.rules.length > 0 && (
                <div style={{ fontSize: 9.5, color: C.muted, marginTop: 8, paddingTop: 6, borderTop: `1px solid ${C.border}` }}>
                  {p.rules.length} default alert rule{p.rules.length > 1 ? 's' : ''}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 2 — Draw Zone ────────────────────────────────────────────────────────

const MAP_CENTER: [number, number] = [52.3144, 4.9415];
const ZONE_DRAW_MAP_MIN_HEIGHT = 320;

function Step2Draw({ wiz, dispatch, C }: { wiz: WizardState; dispatch: React.Dispatch<WizardAction>; C: ColorPalette }) {
  const vertices  = wiz.drawVertices;
  const completed = !!wiz.geometry;
  const {
    basemaps,
    basemapIds,
    defaultBasemap,
    geocoding,
    loading: basemapsLoading,
    error: basemapsError,
  } = useMapProviderConfig();
  const [basemap, setBasemap] = useState<MapBasemapId>('dark');
  const [usingBasemapFallback, setUsingBasemapFallback] = useState(false);
  const tileErrorCount = useRef(0);
  const activeBasemap = basemaps[basemap] ? basemap : defaultBasemap;
  const firstBasemap = basemapIds[0];
  const tileLayer = basemaps[activeBasemap] ?? (firstBasemap ? basemaps[firstBasemap] : undefined);
  const renderedTileLayer = usingBasemapFallback && tileLayer?.fallback
    ? tileLayer.fallback
    : tileLayer;

  useEffect(() => {
    if (!basemaps[basemap] && basemaps[defaultBasemap]) {
      setBasemap(defaultBasemap);
    }
  }, [basemap, basemaps, defaultBasemap]);

  useEffect(() => {
    tileErrorCount.current = 0;
    setUsingBasemapFallback(false);
  }, [activeBasemap, tileLayer?.tileUrl]);

  const handleTileError = useCallback(() => {
    if (!tileLayer?.fallback || usingBasemapFallback) return;
    tileErrorCount.current += 1;
    if (tileErrorCount.current >= 3) setUsingBasemapFallback(true);
  }, [tileLayer?.fallback, usingBasemapFallback]);

  const leafletPositions = useCallback(
    (verts: number[][]): [number, number][] => verts.map(([lng, lat]) => [lat, lng] as [number, number]),
    []
  );

  const drawHint = (() => {
    if (wiz.drawMode === 'circle') return 'Click and drag from the centre point, or click once and set the radius manually.';
    if (wiz.drawMode === 'rectangle') return 'Click one corner, then click the opposite corner to create the rectangle.';
    if (wiz.drawMode === 'corridor') return 'Click corridor path points. Double-click after at least 2 points to create the corridor.';
    return 'Click to place vertices. Double-click to close the polygon. Minimum 3 points required.';
  })();
  const shapeLabel = wiz.drawMode.charAt(0).toUpperCase() + wiz.drawMode.slice(1);

  function setCircleRadius(radiusM: number) {
    const next = Math.max(1, Math.min(5000, Math.round(radiusM)));
    const center = vertices[0];
    dispatch({
      type: 'SET_CIRCLE_RADIUS',
      radiusM: next,
      geometry: center ? circleGeometry(center, next) : wiz.geometry,
    });
  }

  const DRAW_MODES: { id: WizardState['drawMode']; label: string; icon: React.ReactNode }[] = [
    { id: 'polygon',   label: 'Polygon',   icon: <Icons.Triangle size={12} /> },
    { id: 'rectangle', label: 'Rectangle', icon: <Icons.Square   size={12} /> },
    { id: 'circle',    label: 'Circle',    icon: <Icons.Circle   size={12} /> },
    { id: 'corridor',  label: 'Corridor',  icon: <Icons.Flow     size={12} /> },
  ];

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Draw the zone on the map</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
        {drawHint}
      </div>

      {/* Drawing toolbar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
        {DRAW_MODES.map(m => (
          <button
            key={m.id}
            onClick={() => dispatch({ type: 'SET_DRAW_MODE', mode: m.id })}
            style={{
              fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 4,
              background: wiz.drawMode === m.id ? C.amberDim : 'transparent',
              border: `1px solid ${wiz.drawMode === m.id ? `${C.amber}55` : C.border2}`,
              color: wiz.drawMode === m.id ? C.amber : C.sub,
              cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            {m.icon}
            {m.label}
          </button>
        ))}
        <div style={{ display: 'flex', gap: 4, marginLeft: 10, paddingLeft: 10, borderLeft: `1px solid ${C.border2}` }}>
          {basemapIds.map(key => {
            const selected = basemap === key;
            return (
              <button
                key={key}
                onClick={() => setBasemap(key)}
                style={{
                  fontSize: 10, fontWeight: 700, padding: '5px 8px', borderRadius: 4,
                  background: selected ? C.amberDim : 'transparent',
                  border: `1px solid ${selected ? C.amber + '66' : C.border2}`,
                  color: selected ? C.amber : C.sub,
                  cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
                }}
              >
                {BASEMAP_LABELS[key]}
              </button>
            );
          })}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {completed && (
            <span style={{ fontSize: 11, color: C.green, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icons.Check size={12} /> {shapeLabel} complete
            </span>
          )}
          {!completed && vertices.length > 0 && (
            <span style={{ fontSize: 11, color: C.muted }}>
              {vertices.length} vert{vertices.length > 1 ? 'ices' : 'ex'}
              {vertices.length >= 3 ? ' — double-click to close' : ''}
            </span>
          )}
          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Refresh size={11} />} onClick={() => dispatch({ type: 'RESET_DRAW' })}>
            Clear
          </Btn>
        </div>
      </div>

      {wiz.drawMode === 'circle' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
          padding: '8px 10px', border: `1px solid ${C.border2}`, borderRadius: 5,
          background: C.surface,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.text }}>
            Radius
          </span>
          <input
            type="range"
            min={5}
            max={500}
            step={5}
            value={Math.min(500, wiz.circleRadiusM)}
            onChange={e => setCircleRadius(+e.target.value)}
            style={{ flex: 1, accentColor: C.amber }}
          />
          <input
            type="number"
            min={1}
            max={5000}
            value={wiz.circleRadiusM}
            onChange={e => setCircleRadius(+e.target.value || 1)}
            style={{
              width: 86, padding: '5px 7px', borderRadius: 4,
              border: `1px solid ${C.border3}`, background: C.panel,
              color: C.text, fontSize: 11, fontFamily: 'monospace',
            }}
          />
          <span style={{ fontSize: 10, color: C.muted }}>m</span>
        </div>
      )}

      {/* Map */}
      <div data-testid="zone-draw-map" style={{ flex: 1, minHeight: ZONE_DRAW_MAP_MIN_HEIGHT, borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.border2}` }}>
        <MapContainer
          center={MAP_CENTER} zoom={17}
          doubleClickZoom={false}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          attributionControl={false}
        >
          {renderedTileLayer && (
            <TileLayer
              key={`${activeBasemap}:${usingBasemapFallback ? 'fallback' : 'primary'}`}
              url={renderedTileLayer.tileUrl}
              attribution={renderedTileLayer.attribution}
              maxZoom={renderedTileLayer.maxZoom}
              maxNativeZoom={usingBasemapFallback ? undefined : tileLayer?.maxNativeZoom}
              eventHandlers={{ tileerror: handleTileError }}
            />
          )}
          <ZoomControl position="topright" />
          {!renderedTileLayer && (
            <div style={{
              position: 'absolute', left: 12, bottom: 12, zIndex: 500,
              maxWidth: 360, padding: '8px 10px', borderRadius: 5,
              background: `${C.panel}f2`, border: `1px solid ${C.border2}`,
              color: C.muted, fontSize: 11, lineHeight: 1.4,
              fontFamily: "'Albert Sans', sans-serif",
            }}>
              {basemapsLoading ? 'Loading map provider configuration...' : basemapsError ?? 'Map provider configuration is unavailable.'}
            </div>
          )}
          <MapLocationSearch C={C} geocoding={geocoding} />

          <DrawingController
            dispatch={dispatch}
            drawMode={wiz.drawMode}
            vertices={vertices}
            circleRadiusM={wiz.circleRadiusM}
            isCompleted={completed}
          />

          {/* Vertex markers */}
          {vertices.map(([lng, lat], i) => (
            <CircleMarker
              key={i}
              center={[lat, lng]}
              radius={i === 0 ? 6 : 4}
              pathOptions={{ color: C.amber, fillColor: C.amber, fillOpacity: 1, weight: 2 }}
            />
          ))}

          {/* Edge preview while drawing */}
          {vertices.length >= 2 && !completed && wiz.drawMode !== 'circle' && (
            <Polyline
              positions={leafletPositions(vertices)}
              pathOptions={{ color: C.amber, dashArray: '6,4', weight: 2 }}
            />
          )}

          {/* Closing line hint */}
          {vertices.length >= 3 && !completed && wiz.drawMode === 'polygon' && (
            <Polyline
              positions={[leafletPositions(vertices)[vertices.length - 1], leafletPositions(vertices)[0]]}
              pathOptions={{ color: C.amber, dashArray: '3,5', weight: 1.5, opacity: 0.4 }}
            />
          )}

          {wiz.drawMode === 'circle' && vertices[0] && (
            <Circle
              center={[vertices[0][1], vertices[0][0]]}
              radius={wiz.circleRadiusM}
              pathOptions={{ color: C.amber, fillColor: C.amber, fillOpacity: completed ? 0.18 : 0.12, weight: 2.5 }}
            />
          )}

          {/* Completed polygon */}
          {completed && wiz.geometry?.type === 'Polygon' && (
            <Polygon
              positions={leafletPositions((wiz.geometry.coordinates as number[][][])[0])}
              pathOptions={{ color: C.amber, fillColor: C.amber, fillOpacity: 0.18, weight: 2.5 }}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
}

// ── Step 3 — Configure Alerts ─────────────────────────────────────────────────

function Step3Alerts({ wiz, presetDefs, dispatch, C }: {
  wiz: WizardState;
  presetDefs: PresetView[];
  dispatch: React.Dispatch<WizardAction>;
  C: ColorPalette;
}) {
  const def = presetDefs.find(p => p.type === wiz.preset);

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Configure alert thresholds</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
        Defaults come from the{' '}
        <span style={{ color: C.amber, fontWeight: 600 }}>{def?.name ?? 'selected'}</span> preset.
        Enable or adjust each rule independently.
      </div>

      {wiz.alertRules.length === 0 ? (
        <div style={{
          background: C.panel, border: `1px dashed ${C.border3}`, borderRadius: 6,
          padding: '28px 24px', textAlign: 'center', color: C.muted, fontSize: 12,
        }}>
          <Icons.Info size={18} />
          <div style={{ marginTop: 8 }}>No default rules for this preset. All alert thresholds can be added after zone activation.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {wiz.alertRules.map(rule => (
            <div
              key={rule.id}
              style={{
                background: rule.enabled ? C.panel : `${C.panel}80`,
                border: `1px solid ${rule.enabled ? C.border2 : C.border}`,
                borderRadius: 6, padding: 16, opacity: rule.enabled ? 1 : 0.65,
                transition: 'all 150ms',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 5, flexShrink: 0,
                  background: rule.enabled ? C.amberDim : C.faint,
                  color: rule.enabled ? C.amber : C.muted,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icons.Alert size={14} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{rule.label}</div>
                  <div style={{ fontSize: 10.5, color: C.muted }}>{rule.condition}</div>
                </div>
                <Toggle
                  enabled={rule.enabled}
                  onClick={() => dispatch({ type: 'TOGGLE_RULE', ruleId: rule.id })}
                  C={C}
                />
              </div>
              {!rule.tokenOnly && (
                <div style={{ display: 'flex', gap: 10, paddingLeft: 42 }}>
                  <ThresholdInput
                    label={
                      rule.alertType === 'restricted_zone_gathering' ? 'Person count >='
                        : rule.unit === 'persons' ? 'Cluster size ≥'
                        : 'Exceeds'
                    }
                    value={rule.threshold}
                    unit={rule.unit}
                    onChange={v => dispatch({ type: 'UPDATE_RULE', ruleId: rule.id, key: 'threshold', value: v })}
                    min={1} max={rule.unit === 'persons' ? 500 : 100}
                    C={C}
                  />
                  {/* Restricted Zone Gathering is instant by design - no sustain window applies. */}
                  {rule.alertType !== 'restricted_zone_gathering' && (
                    <ThresholdInput
                      label="For ≥"
                      value={rule.windowMinutes}
                      unit="min"
                      onChange={v => dispatch({ type: 'UPDATE_RULE', ruleId: rule.id, key: 'windowMinutes', value: v })}
                      min={1} max={60}
                      C={C}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{
        marginTop: 14, background: C.panel, border: `1px dashed ${C.border3}`, borderRadius: 6,
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: C.muted,
      }}>
        <Icons.Info size={12} />
        Additional threshold rules and flagged-token alerts can be added from the zone detail view after activation.
      </div>
    </div>
  );
}

// ── Step 4 — Assign & Activate ────────────────────────────────────────────────

const RISK_LEVELS: { value: RiskLevel; label: string }[] = [
  { value: 'low',      label: 'Low'      },
  { value: 'medium',   label: 'Medium'   },
  { value: 'high',     label: 'High'     },
  { value: 'critical', label: 'Critical' },
];

const ACTIVATION_MODES = [
  { id: 'live',      label: 'Live',      desc: 'Activate immediately. Zone enters monitoring on save.' },
  { id: 'scheduled', label: 'Scheduled', desc: 'Set start and end times. Zone is armed for the scheduled window.' },
  { id: 'manual',    label: 'Manual',    desc: 'Saved inactive for an explicit activation workflow.' },
] as const;

function Step4Assign({
  wiz,
  dispatch,
  C,
  teams,
  defaultTeamId,
  hasOperationContext,
  assignmentContextNote,
}: {
  wiz: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  C: ColorPalette;
  teams: CrowdTeam[];
  defaultTeamId: string | null;
  hasOperationContext: boolean;
  assignmentContextNote?: string | null;
}) {
  const teamById = buildTeamById(teams);
  const defaultTeam = defaultTeamId ? teamById.get(defaultTeamId) : null;
  const selectableTeams = hasOperationContext ? teams : [];

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Assign and activate</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
        {hasOperationContext
          ? 'Name the zone, set capacity and risk level, and choose an Admin Teams assignment for this operation.'
          : 'Name the zone, set capacity and risk level.'}
      </div>
      {assignmentContextNote && (
        <div
          data-testid="zone-assignment-context-note"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginBottom: 18,
            padding: '10px 12px',
            borderRadius: 5,
            border: `1px solid ${C.border}`,
            background: C.faint,
            color: C.sub,
            fontSize: 11.5,
            lineHeight: 1.45,
          }}
        >
          <span style={{ color: C.teal, marginTop: 1, flexShrink: 0, display: 'flex' }}>
            <Icons.Info size={13} />
          </span>
          <span>{assignmentContextNote}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Zone name *" C={C}>
            <input
              data-testid="zone-name-input"
              value={wiz.name}
              maxLength={80}
              onChange={e => dispatch({ type: 'SET_FIELD', field: 'name', value: e.target.value })}
              style={inputCss(C)}
              placeholder="e.g. North Stand Terrace"
            />
          </FormField>

          <FormField label="Description (optional)" C={C}>
            <textarea
              value={wiz.description}
              maxLength={280}
              rows={3}
              onChange={e => dispatch({ type: 'SET_FIELD', field: 'description', value: e.target.value })}
              style={{ ...inputCss(C), resize: 'vertical', minHeight: 70 }}
              placeholder="Operational context or briefing notes for this zone."
            />
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Capacity (persons) *" C={C}>
              <input
                data-testid="zone-capacity-input"
                type="number" min={1}
                value={wiz.capacity}
                onChange={e => dispatch({ type: 'SET_FIELD', field: 'capacity', value: Number(e.target.value) })}
                style={inputCss(C)}
              />
            </FormField>
            <FormField label="Risk level *" C={C}>
              <select
                value={wiz.riskLevel}
                onChange={e => dispatch({ type: 'SET_FIELD', field: 'riskLevel', value: e.target.value as RiskLevel })}
                style={inputCss(C)}
              >
                {RISK_LEVELS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </FormField>
          </div>

          {hasOperationContext && (
            <FormField label="Assigned operations team" C={C}>
              <select
                value={wiz.assignedTeamId}
                onChange={e => dispatch({ type: 'SET_FIELD', field: 'assignedTeamId', value: e.target.value })}
                style={inputCss(C)}
              >
                <option value="">
                  {defaultTeam
                    ? `Same as operation default (${defaultTeam.name})`
                    : 'Unassigned'}
                </option>
                {defaultTeam && (
                  <option value={UNASSIGNED_TEAM_VALUE}>No team assigned</option>
                )}
                {selectableTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
              {selectableTeams.length === 0 && (
                <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
                  No active teams are configured in Crowd Admin. The zone will remain unassigned.
                </div>
              )}
            </FormField>
          )}
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
            Activation mode
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ACTIVATION_MODES.map(m => (
              <label
                key={m.id}
                style={{
                  display: 'flex', gap: 10, padding: 12, borderRadius: 5, cursor: 'pointer',
                  background: wiz.activation === m.id ? C.amberDim : C.panel,
                  border: `1px solid ${wiz.activation === m.id ? `${C.amber}55` : C.border2}`,
                }}
              >
                <input
                  type="radio" name="activation" checked={wiz.activation === m.id}
                  onChange={() => dispatch({ type: 'SET_FIELD', field: 'activation', value: m.id })}
                  style={{ marginTop: 3, accentColor: C.amber }}
                />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{m.label}</div>
                  <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{m.desc}</div>
                </div>
              </label>
            ))}
          </div>
          {wiz.activation === 'scheduled' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 12 }}>
              <FormField label="Scheduled start *" C={C}>
                <input
                  data-testid="zone-activation-scheduled-start"
                  type="datetime-local"
                  value={wiz.activationScheduledStart}
                  onChange={e => dispatch({ type: 'SET_FIELD', field: 'activationScheduledStart', value: e.target.value })}
                  style={inputCss(C)}
                />
              </FormField>
              <FormField label="Scheduled end *" C={C}>
                <input
                  data-testid="zone-activation-scheduled-end"
                  type="datetime-local"
                  value={wiz.activationScheduledEnd}
                  onChange={e => dispatch({ type: 'SET_FIELD', field: 'activationScheduledEnd', value: e.target.value })}
                  style={inputCss(C)}
                />
              </FormField>
              {wiz.activationScheduledStart && wiz.activationScheduledEnd && !localDateTimeToIso(wiz.activationScheduledStart) && (
                <div style={{ fontSize: 10.5, color: C.ember }}>Scheduled start must be a valid date and time.</div>
              )}
              {wiz.activationScheduledStart && wiz.activationScheduledEnd && localDateTimeToIso(wiz.activationScheduledStart) && localDateTimeToIso(wiz.activationScheduledEnd) && new Date(localDateTimeToIso(wiz.activationScheduledEnd)!) <= new Date(localDateTimeToIso(wiz.activationScheduledStart)!) && (
                <div style={{ fontSize: 10.5, color: C.ember }}>Scheduled end must be after scheduled start.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 5 — Review & Save ────────────────────────────────────────────────────

function Step5Review({ wiz, presetDefs, venues, teams, defaultTeamId, hasOperationContext, onConfirm, saving, error, C }: {
  wiz:       WizardState;
  presetDefs: PresetView[];
  venues:    Venue[];
  teams:     CrowdTeam[];
  defaultTeamId: string | null;
  hasOperationContext: boolean;
  onConfirm: () => void;
  saving:    boolean;
  error:     string | null;
  C:         ColorPalette;
}) {
  const def        = presetDefs.find(p => p.type === wiz.preset);
  const activeRules = wiz.alertRules.filter(r => r.enabled);
  const selectedVenue = venues.find(venue => venue.id === wiz.venueId);
  const teamById = buildTeamById(teams);
  const defaultTeam = defaultTeamId ? teamById.get(defaultTeamId) : null;
  const selectedTeamName = wiz.assignedTeamId === UNASSIGNED_TEAM_VALUE
    ? 'Unassigned'
    : wiz.assignedTeamId
    ? teamById.get(wiz.assignedTeamId)?.name ?? 'Selected team'
    : hasOperationContext && defaultTeam
    ? `${defaultTeam.name} (operation default)`
    : 'Unassigned';
  const scheduledStartIso = wiz.activation === 'scheduled' ? localDateTimeToIso(wiz.activationScheduledStart) : null;
  const scheduledEndIso = wiz.activation === 'scheduled' ? localDateTimeToIso(wiz.activationScheduledEnd) : null;
  const activationLabel = wiz.activation === 'scheduled'
    ? 'Scheduled'
    : wiz.activation === 'manual'
      ? 'Manual'
      : 'Live';
  const activationDetail = wiz.activation === 'scheduled'
    ? scheduledStartIso && scheduledEndIso
      ? `${formatDateTime(scheduledStartIso)} - ${formatDateTime(scheduledEndIso)}`
      : 'Schedule window required'
    : wiz.activation === 'manual'
      ? 'Saved inactive for explicit activation'
      : 'Monitoring starts on save';
  const geometrySummary = wiz.drawMode === 'circle'
    ? `Circle - ${wiz.circleRadiusM} m radius`
    : wiz.drawMode === 'rectangle'
      ? 'Rectangle - 4 corners'
      : wiz.drawMode === 'corridor'
        ? `Corridor - ${wiz.drawVertices.length} path points`
        : `Polygon - ${wiz.geometry ? ((wiz.geometry.coordinates as number[][][])[0].length - 1) : 0} vertices`;

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>Review and confirm</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>
        Confirm the zone's venue, operating role, monitoring readiness, and alert thresholds before saving.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <Panel C={C} style={{ padding: 18 }}>
          <KLabel C={C}>Identity</KLabel>
          {([
            ['Venue',       venueLabel(selectedVenue)],
            ['Name',        wiz.name || '—'],
            ['Zone role preset', def?.name ?? '—'],
            ['Description', wiz.description || '—'],
          ] as [string, string][]).map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: C.text, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{v}</span>
            </div>
          ))}
        </Panel>

        <Panel C={C} style={{ padding: 18 }}>
          <KLabel C={C}>Operations</KLabel>
          {([
            ['Capacity',     `${wiz.capacity.toLocaleString()} persons`],
            ['Risk level',   wiz.riskLevel],
            ['Team',         selectedTeamName],
            ['Activation',   activationLabel],
            ['Activation window', activationDetail],
          ] as [string, string][]).map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: C.text, textTransform: 'capitalize' }}>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 11, color: C.muted }}>Zone type</span>
            <span style={{
              fontSize: 11.5,
              fontWeight: 700,
              color: wiz.temporary ? C.amber : C.green,
              textAlign: 'right',
            }}>
              {wiz.temporary ? 'Operation-specific zone' : 'Permanent venue zone'}
            </span>
          </div>
        </Panel>
      </div>

      {wiz.geometry && (
        <Panel C={C} style={{ padding: 18, marginBottom: 14 }}>
          <KLabel C={C}>Zone Geometry</KLabel>
          <GeometryThumbnail geometry={wiz.geometry} C={C} />
          <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
            {geometrySummary}
          </div>
        </Panel>
      )}

      {activeRules.length > 0 && (
        <Panel C={C} style={{ padding: 18, marginBottom: 14 }}>
          <KLabel C={C}>Alert Rules ({activeRules.length})</KLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeRules.map(r => (
              <div
                key={r.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', background: C.surface,
                  border: `1px solid ${C.border}`, borderRadius: 4,
                }}
              >
                <span style={{ color: C.amber, flexShrink: 0 }}><Icons.Alert size={12} /></span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: C.text, minWidth: 160 }}>{r.label}</span>
                <span style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>
                  {r.tokenOnly
                    ? 'Fires on anonymous token entry'
                    : r.alertType === 'restricted_zone_gathering'
                      ? `${r.threshold} ${r.unit} detected`
                      : `${r.threshold} ${r.unit} for ${r.windowMinutes}+ min`}
                </span>
                <Icons.Check size={13} />
              </div>
            ))}
          </div>
        </Panel>
      )}

      {error && (
        <div style={{
          background: C.emberDim, border: `1px solid ${C.ember}55`, borderRadius: 5,
          padding: '10px 14px', fontSize: 12, color: C.ember, marginBottom: 10,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn
          C={C} variant="primary"
          icon={<Icons.Check size={13} />}
          onClick={onConfirm}
          disabled={saving}
        >
          {saving ? 'Activating…' : 'Confirm & Activate'}
        </Btn>
      </div>
    </div>
  );
}

// ── Zone creation wizard ──────────────────────────────────────────────────────

interface ZoneCreationWizardProps {
  onClose:              () => void;
  onSuccess:            (zone?: Zone) => void | Promise<void>;
  C:                    ColorPalette;
  createForVenueId:     string | null;
  createForOperationId: string | null;
  assignmentContextNote?: string | null;
}

function unwrapZoneCreateResponse(body: Zone | { data?: Zone }): Zone | undefined {
  return (body as { data?: Zone }).data ?? (body as Zone);
}

export function ZoneCreationWizard({ onClose, onSuccess, C, createForVenueId, createForOperationId, assignmentContextNote = null }: ZoneCreationWizardProps) {
  const [wiz, dispatch] = useReducer(wizardReducer, INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const wizardContextKeyRef = useRef<string | null>(null);
  const { venues, loading: venuesLoading, createVenue } = useVenues();
  const { taxonomy, loading: taxonomyLoading, error: taxonomyError } = useCrowdTaxonomy();
  const { teams } = useTeams();
  const presetDefs = useMemo(() => buildPresetDefs(taxonomy), [taxonomy]);
  const { selectedOperation } = useApp();
  const activeTeams = useMemo(() => activeCrowdTeams(teams), [teams]);
  const operationVenueId = createForOperationId && selectedOperation?.id === createForOperationId
    ? selectedOperation.venueId
    : null;
  const operationDefaultTeamId = createForOperationId && selectedOperation?.id === createForOperationId
    ? selectedOperation.defaultTeamId ?? null
    : null;

  useEffect(() => {
    const contextKey = createForVenueId
      ? `venue:${createForVenueId}`
      : createForOperationId
        ? `operation:${createForOperationId}:${operationVenueId ?? 'pending'}`
        : 'zone-management';
    if (wizardContextKeyRef.current === contextKey) return;
    wizardContextKeyRef.current = contextKey;

    if (createForVenueId) {
      dispatch({
        type: 'INIT_CONTEXT',
        venueId: createForVenueId,
        venueLocked: true,
        temporary: false,
        step: 1,
        assignedTeamId: '',
      });
      return;
    }
    if (createForOperationId) {
      dispatch({
        type: 'INIT_CONTEXT',
        venueId: operationVenueId,
        venueLocked: Boolean(operationVenueId),
        temporary: true,
        step: operationVenueId ? 1 : 0,
        assignedTeamId: '',
      });
      return;
    }
    dispatch({
      type: 'INIT_CONTEXT',
      venueId: null,
      venueLocked: false,
      temporary: false,
      step: 0,
      assignedTeamId: '',
    });
  }, [createForVenueId, createForOperationId, operationVenueId]);

  const canNext = (() => {
    if (wiz.step === 0) return !!wiz.venueId;
    if (wiz.step === 1) return !!wiz.preset;
    if (wiz.step === 2) return !!wiz.geometry;
    if (wiz.step === 4) {
      if (!wiz.name.trim() || wiz.capacity < 1) return false;
      if (wiz.activation !== 'scheduled') return true;
      const scheduledStart = localDateTimeToIso(wiz.activationScheduledStart);
      const scheduledEnd = localDateTimeToIso(wiz.activationScheduledEnd);
      return Boolean(scheduledStart && scheduledEnd && new Date(scheduledEnd) > new Date(scheduledStart));
    }
    return true;
  })();

  function handleCancel() {
    if (wiz.step === 0 || (wiz.step === 1 && wiz.venueLocked)) { onClose(); return; }
    setShowDiscard(true);
  }

  async function handleConfirm() {
    if (!wiz.venueId || !wiz.preset || !wiz.geometry) return;
    setSaving(true);
    setError(null);
    try {
      const inheritedTeamId = createForOperationId ? operationDefaultTeamId : null;
      const assignedTeamId = createForOperationId
        ? wiz.assignedTeamId === UNASSIGNED_TEAM_VALUE
          ? null
          : wiz.assignedTeamId || inheritedTeamId
        : undefined;
      const assignedTeamInheritedFromDefault = Boolean(createForOperationId && !wiz.assignedTeamId && inheritedTeamId);
      const body = await post<Zone | { data?: Zone }>('/crowd/zones', {
        name:         wiz.name.trim(),
        type:         wiz.preset,
        geometry:     wiz.geometry,
        capacity:     wiz.capacity,
        riskLevel:    wiz.riskLevel,
        venueId:      wiz.venueId,
        temporary:    wiz.temporary,
        activationMode: wiz.activation,
        ...(wiz.activation === 'scheduled' ? {
          activationScheduledStart: localDateTimeToIso(wiz.activationScheduledStart),
          activationScheduledEnd:   localDateTimeToIso(wiz.activationScheduledEnd),
        } : {}),
        createContext: createForOperationId ? 'operation' : createForVenueId ? 'venue' : 'zone-management',
        ...(createForOperationId ? {
          assignedTeamId,
          assignedTeamInheritedFromDefault,
          operationId: createForOperationId,
        } : {}),
        alertRules:    wiz.alertRules.map(wizardRulePayload),
      });
      dispatch({ type: 'RESET' });
      await onSuccess(unwrapZoneCreateResponse(body));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create zone. Check Core API is running.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="zone-creation-wizard" style={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg, fontFamily: "'Albert Sans', sans-serif" }}>

      {/* Header */}
      <div style={{
        padding: '14px 24px', borderBottom: `1px solid ${C.border2}`, background: C.surface,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Create New Zone</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace' }}>Step {wiz.step + 1} of 6</span>
          <button onClick={handleCancel} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: "'Albert Sans', sans-serif" }}>
            <Icons.X size={14} /> Cancel
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div style={{ padding: '16px 24px', background: C.surface, borderBottom: `1px solid ${C.border2}`, flexShrink: 0 }}>
        <WizardStepper step={wiz.step} C={C} />
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: wiz.step === 2 ? 'hidden' : 'auto',
        padding: '28px 32px',
        display: wiz.step === 2 ? 'flex' : 'block',
        flexDirection: wiz.step === 2 ? 'column' : undefined,
      }}>
        {wiz.step === 0 && (
          <Step0Venue
            wiz={wiz}
            venues={venues}
            loading={venuesLoading}
            createVenue={createVenue}
            dispatch={dispatch}
            C={C}
          />
        )}
        {wiz.step === 1 && (
          taxonomyLoading || taxonomyError ? (
            <div style={{ color: taxonomyError ? C.ember : C.muted, fontSize: FONT_SIZE.body }}>
              {taxonomyError ?? 'Loading zone presets...'}
            </div>
          ) : (
            <Step1Preset wiz={wiz} presetDefs={presetDefs} dispatch={dispatch} C={C} />
          )
        )}
        {wiz.step === 2 && <Step2Draw   wiz={wiz} dispatch={dispatch} C={C} />}
        {wiz.step === 3 && <Step3Alerts wiz={wiz} presetDefs={presetDefs} dispatch={dispatch} C={C} />}
        {wiz.step === 4 && (
          <Step4Assign
            wiz={wiz}
            dispatch={dispatch}
            C={C}
            teams={activeTeams}
            defaultTeamId={operationDefaultTeamId}
            hasOperationContext={Boolean(createForOperationId)}
            assignmentContextNote={assignmentContextNote}
          />
        )}
        {wiz.step === 5 && (
          <Step5Review
            wiz={wiz} presetDefs={presetDefs} venues={venues} teams={activeTeams}
            defaultTeamId={operationDefaultTeamId}
            hasOperationContext={Boolean(createForOperationId)}
            onConfirm={handleConfirm}
            saving={saving} error={error} C={C}
          />
        )}
      </div>

      {/* Footer nav */}
      <div style={{
        padding: '12px 24px', background: C.surface, borderTop: `1px solid ${C.border2}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
      }}>
        <Btn
          C={C}
          variant="ghost"
          icon={(wiz.step > 0 && !(wiz.step === 1 && wiz.venueLocked)) ? <Icons.ChevL size={12} /> : undefined}
          onClick={(wiz.step > 0 && !(wiz.step === 1 && wiz.venueLocked)) ? () => dispatch({ type: 'GO_TO_STEP', step: wiz.step - 1 }) : handleCancel}
        >
          {wiz.step === 0 || (wiz.step === 1 && wiz.venueLocked) ? 'Cancel' : 'Back'}
        </Btn>
        {wiz.step === 2 && !wiz.geometry && (
          <span style={{ fontSize: 10.5, color: C.muted }}>Draw a polygon on the map to continue</span>
        )}
        {wiz.step < 5 && (
          <Btn C={C} variant="primary" disabled={!canNext} onClick={() => dispatch({ type: 'GO_TO_STEP', step: wiz.step + 1 })}>
            Continue →
          </Btn>
        )}
      </div>

      {/* Discard confirm */}
      {showDiscard && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 8,
            padding: 24, maxWidth: 360, width: '90%', fontFamily: "'Albert Sans', sans-serif",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>Discard this zone?</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 18 }}>
              All wizard data will be lost. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn C={C} variant="ghost" onClick={() => setShowDiscard(false)}>Keep editing</Btn>
              <Btn C={C} variant="danger" onClick={() => { dispatch({ type: 'RESET' }); setShowDiscard(false); onClose(); }}>
                Discard zone
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Zone list ─────────────────────────────────────────────────────────────────

function ZoneListView({ C, mode, onCreateZone, canCreate, canArchive, onZoneClick }: {
  C:            ColorPalette;
  mode:         ZoneScreenMode;
  onCreateZone: () => void;
  canCreate:    boolean;
  canArchive:   boolean;
  onZoneClick:  (zone: Zone) => void;
}) {
  const isCatalogMode = mode === 'catalog';
  const navigate = useNavigate();
  const { zoneTypeLabel, zoneTypeCode } = useCrowdTaxonomy();
  const { isMock } = useMockMode();
  const { currentUser, selectedOperation, openOperationModal } = useApp();
  const { teams } = useTeams();
  const densityData = useDensityData();
  const liveZoneMetrics = useLiveZoneMetrics(
    isCatalogMode ? null : selectedOperation?.id,
    Boolean(!isMock && (isCatalogMode || selectedOperation?.status === 'active')),
    2000,
  );
  const projectedSnapshots = useMemo(() => zoneMetricsDensitySnapshots(liveZoneMetrics.rows), [liveZoneMetrics.rows]);
  const snapshots = useMemo(
    () => isMock ? densityData.snapshots : { ...densityData.snapshots, ...projectedSnapshots },
    [densityData.snapshots, isMock, projectedSnapshots],
  );
  const alertOperationId = isCatalogMode ? null : selectedOperation?.id ?? null;
  const { alerts } = useAlertData(alertOperationId, {
    enabled: isCatalogMode || Boolean(selectedOperation),
    requireOperation: !isCatalogMode,
    zoneIds: isCatalogMode ? undefined : selectedOperation?.assignedZoneIds ?? [],
  });

  const [allZones,     setAllZones]     = useState<Zone[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [tab,          setTab]          = useState('operation');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk,   setFilterRisk]   = useState('all');
  const [filterTeam,   setFilterTeam]   = useState('all');
  const [search,       setSearch]       = useState('');
  const [archiving,    setArchiving]    = useState<string | null>(null);
  const [toast,        setToast]        = useState<string | null>(null);
  useCrowdFilterActivity({
    page: isCatalogMode ? '/admin/zones' : '/zones',
    operationId: isCatalogMode ? null : selectedOperation?.id,
    filterValues: {
      tab,
      status: filterStatus,
      risk: filterRisk,
      team: filterTeam !== 'all' ? 'selected' : undefined,
    },
    searchApplied: Boolean(search.trim()),
  });

  async function fetchZones() {
    if (isCatalogMode) {
      if (isMock) {
        setAllZones(MOCK_ZONES.filter(zone => Boolean(zone.venueId)));
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const body = await get<Zone[] | { data?: Zone[] }>(
          `/geo/zones?${crowdQuery({ archiveStatus: 'all' })}`,
        );
        setAllZones(Array.isArray(body) ? body : (body.data ?? []));
      } catch {
        setAllZones([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!selectedOperation) { setAllZones([]); setLoading(false); return; }
    if (isMock) {
      setAllZones(MOCK_ZONES.filter(zone => selectedOperation.assignedZoneIds.includes(zone.id)));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const body = await get<Zone[] | { data?: Zone[] }>(
        `/operations/${selectedOperation.id}/zones?${crowdQuery({ archiveStatus: 'all' })}`,
      );
      setAllZones(Array.isArray(body) ? body : (body.data ?? []));
    } catch {
      setAllZones([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchZones(); }, [isMock, mode, selectedOperation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopedZones: Zone[] = isCatalogMode
    ? allZones.filter((zone: Zone) => Boolean(zone.venueId))
    : selectedOperation
    ? allZones.filter((zone: Zone) => selectedOperation.assignedZoneIds.includes(zone.id))
    : [];
  const activeTeams = useMemo(() => activeCrowdTeams(teams), [teams]);
  const teamById = useMemo(() => buildTeamById(activeTeams), [activeTeams]);
  const teamNameForZone = useCallback((zone: Zone) => (
    isCatalogMode
      ? zone.assignedTeam
      : operationTeamNameForZone(selectedOperation, zone.id, teamById, zone.assignedTeam)
  ), [isCatalogMode, selectedOperation, teamById]);
  const currentUserTeamIds = useMemo(
    () => activeTeams.filter(team => team.memberUserIds.includes(currentUser.id)).map(team => team.id),
    [activeTeams, currentUser.id],
  );
  const isAssignedToCurrentUser = useCallback((zone: Zone) => {
    const teamId = operationTeamIdForZone(selectedOperation, zone.id);
    if (teamId && currentUserTeamIds.includes(teamId)) return true;
    const teamName = teamNameForZone(zone);
    return Boolean(currentUser.team && teamName === currentUser.team);
  }, [currentUser.team, currentUserTeamIds, selectedOperation, teamNameForZone]);
  const activeZones = scopedZones.filter((z: Zone) => !z.archivedAt);
  const archivedZones = scopedZones.filter((z: Zone) => !!z.archivedAt);
  const zones: Zone[] = tab === 'archived' ? archivedZones : activeZones;
  const zoneAlertSummaries = useZoneAlertSummaries(
    isCatalogMode ? null : selectedOperation?.id ?? null,
    activeZones.map(zone => zone.id),
    Boolean(!isMock && (isCatalogMode || selectedOperation)),
    isCatalogMode ? 'global' : 'operation',
  );

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleArchive(zone: Zone) {
    setArchiving(zone.id);
    try {
      await post(`/crowd/zones/${zone.id}/archive`);
      showToast(`"${zone.name}" archived`);
      await fetchZones();
    } catch {
      showToast('Archive failed - check Crowd API');
    } finally {
      setArchiving(null);
    }
  }

  // Filter chain
  let filtered = zones;
  if (tab === 'my-zones') filtered = filtered.filter((z: Zone) => isAssignedToCurrentUser(z));
  if (tab === 'critical')     filtered = filtered.filter((z: Zone) => z.riskLevel === 'critical');
  if (filterStatus === 'archived') filtered = filtered.filter((z: Zone) => !!z.archivedAt);
  else if (filterStatus !== 'all') {
    // Filter on the live (utilisation-derived) status, the same value the Status column shows.
    filtered = filtered.filter((z: Zone) => !z.archivedAt && resolveZoneStatus(liveZoneMetrics.byZone[z.id], snapshots[z.id]) === filterStatus);
  }
  if (filterRisk   !== 'all') filtered = filtered.filter((z: Zone) => z.riskLevel === filterRisk);
  if (filterTeam   !== 'all') filtered = filtered.filter((z: Zone) => teamNameForZone(z) === filterTeam);
  if (search.trim())          filtered = filtered.filter((z: Zone) => z.name.toLowerCase().includes(search.toLowerCase()));
  const zonePagination = useLocalPagination(filtered);

  useEffect(() => {
    zonePagination.resetPage();
  }, [tab, filterStatus, filterRisk, filterTeam, search, zonePagination.resetPage]);

  function alertCountForZone(zoneId: string): number {
    if (!isMock) {
      const summary = zoneAlertSummaries.summariesByZoneId.get(zoneId);
      if (summary) return summary.active;
    }
    return activeAlertCountForZoneInMode(alerts, zoneId, selectedOperation, isCatalogMode);
  }

  const alertZones = activeZones.filter((z: Zone) => alertCountForZone(z.id) > 0).length;
  const canRunOperationalActions = !isCatalogMode && allowsOperationalZoneActions(selectedOperation);
  const tabList = isCatalogMode
    ? [
      { id: 'operation',  label: 'Configured Zones', count: activeZones.length },
      { id: 'critical',   label: 'Critical',         count: activeZones.filter((z: Zone) => z.riskLevel === 'critical').length },
      { id: 'archived',   label: 'Archived',         count: archivedZones.length },
    ]
    : [
      { id: 'operation',  label: 'Operation Zones',  count: activeZones.length },
      { id: 'my-zones',   label: 'My Zones',         count: activeZones.filter((z: Zone) => isAssignedToCurrentUser(z)).length },
      { id: 'critical',   label: 'Critical',         count: activeZones.filter((z: Zone) => z.riskLevel === 'critical').length },
      { id: 'archived',   label: 'Archived',         count: archivedZones.length },
    ];
  const canManageOperations = currentUser.role === 'command_supervisor' || currentUser.role === 'system_admin';

  const selectStyle: React.CSSProperties = {
    background: C.surface, border: `1px solid ${C.border2}`, borderRadius: 4,
    padding: '6px 10px', fontSize: 11.5, color: C.text, fontFamily: "'Albert Sans', sans-serif",
    cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 20, fontFamily: "'Albert Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
            {isCatalogMode ? 'Zones' : 'Monitored Zones'}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {isCatalogMode
              ? `${activeZones.length} configured zone${activeZones.length !== 1 ? 's' : ''} - ${archivedZones.length} archived across venues`
              : selectedOperation
              ? `${selectedOperation.name} - ${activeZones.length} active zone${activeZones.length !== 1 ? 's' : ''} - ${archivedZones.length} archived - ${alertZones} with active alerts`
              : 'Select or start an operation to view monitored zones'}
          </div>
        </div>
        {((isCatalogMode && canCreate) || (selectedOperation && canCreate && canRunOperationalActions)) && (
          <Btn C={C} variant="primary" icon={<Icons.Plus size={13} />} onClick={onCreateZone}>
            Create Zone
          </Btn>
        )}
      </div>

      {!isCatalogMode && !selectedOperation ? (
        <Panel C={C} style={{ padding: SPACE[32], textAlign: 'center' }}>
          <Icons.Zones size={SIZE.iconXl} />
          <div style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.text, marginTop: SPACE[12] }}>
            No active operation selected
          </div>
          <div style={{ maxWidth: 520, margin: `${SPACE[6]}px auto 0`, fontSize: FONT_SIZE.body, color: C.muted, lineHeight: LINE_HEIGHT.relaxed }}>
            Operational zones are scoped to the selected operation. Venue-wide zone configuration is available from Administration.
          </div>
          {canManageOperations && (
            <div style={{ marginTop: SPACE[16] }}>
              <Btn C={C} variant="primary" icon={<Icons.Plus size={SIZE.iconSm} />} onClick={() => openOperationModal()}>
                Start Operation
              </Btn>
            </div>
          )}
        </Panel>
      ) : (
      <>
      {/* Tab strip */}
      <div style={{ marginBottom: 14 }}>
        <Tabs C={C} tabs={tabList} active={tab} onChange={setTab} />
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
          <option value="all">All statuses</option>
          <option value="normal">Normal</option>
          <option value="warning">Warning</option>
          <option value="alert">Alert</option>
          <option value="archived">Archived</option>
        </select>
        <select value={filterRisk} onChange={e => setFilterRisk(e.target.value)} style={selectStyle}>
          <option value="all">All risk levels</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)} style={selectStyle}>
          <option value="all">All teams</option>
          {Array.from(new Set(scopedZones.map(z => teamNameForZone(z)).filter(Boolean))).map(t => (
            <option key={t!} value={t!}>{t}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icons.Search size={12} />
          <input
            placeholder="Search zones…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...selectStyle, width: 200 }}
          />
        </div>
      </div>

      {/* Zone table */}
      <Panel C={C}>
        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: 12 }}>
            Loading zones…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: C.muted, fontSize: 12 }}>
            {zones.length === 0 ? (tab === 'archived' ? 'No archived zones found.' : isCatalogMode ? 'No configured venue zones found. Create a zone from the catalog or a venue.' : 'No zones found. Create a zone to get started.') : 'No zones match the current filters.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1.5px solid ${C.border2}` }}>
                {['Zone', 'Type', 'Status', 'Occupancy', 'Density', 'Risk', 'Alerts', 'Team', 'Readiness', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: C.muted,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zonePagination.pageRows.map(zone => {
                const snap  = snapshots[zone.id];
                const metric = liveZoneMetrics.byZone[zone.id];
                const util  = Number(snap?.capacityUtilisation ?? 0);
                const isArchived = !!zone.archivedAt;
                const liveStatus = resolveZoneStatus(metric, snap);
                const stColor = isArchived ? C.muted : zoneStatusColor(liveStatus, C);
                const activationLabel = zoneActivationLabel(zone);
                const activationColor = isArchived ? C.muted : zoneActivationColor(zone, C);
                const alertCount = alertCountForZone(zone.id);

                return (
                  <tr
                    key={zone.id}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', transition: 'background 100ms' }}
                    onMouseEnter={e => (e.currentTarget.style.background = C.faint)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => onZoneClick(zone)}
                  >
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{zone.name}</div>
                      <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginTop: 1 }}>
                        {shortId(zone.id)} · {zoneCode(zone, zoneTypeCode)}
                      </div>
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      <Badge C={C} color={C.sub} bg={C.faint} size={9}>{zoneTypeLabel(zone.type)}</Badge>
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <StatusDot color={stColor} pulse={!isArchived && liveStatus === 'alert'} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: stColor, textTransform: 'capitalize' }}>
                          {isArchived ? 'archived' : zoneStatusLabel(liveStatus).toLowerCase()}
                        </span>
                      </span>
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text, fontFamily: 'monospace' }}>
                        {!isArchived && snap ? snap.occupancyCount.toLocaleString() : '—'}
                      </div>
                      <div style={{ fontSize: 9, color: C.muted }}>of {zone.capacity.toLocaleString()}</div>
                    </td>

                    <td style={{ padding: '11px 14px', minWidth: 100 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ flex: 1, height: 4, background: C.faint, borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', width: `${Math.min(util, 100)}%`,
                            background: stColor, transition: 'width 600ms ease',
                          }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.text, fontFamily: 'monospace', minWidth: 30, textAlign: 'right' }}>
                          {isArchived || !snap ? '—' : formatUtilPct(util, true)}
                        </span>
                      </div>
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: riskColor(zone.riskLevel, C) }}>
                        {zone.riskLevel}
                      </span>
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      {alertCount > 0 ? (
                        <Pill color={alertCountColor(C)}>{alertCount}</Pill>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, fontFamily: 'monospace' }}>—</span>
                      )}
                    </td>

                    <td style={{ padding: '11px 14px', fontSize: 11, color: C.sub }}>
                      {teamNameForZone(zone) ?? '—'}
                    </td>

                    <td style={{ padding: '11px 14px', fontSize: 10.5, color: C.muted, fontFamily: 'monospace' }}>
                      {(isArchived || activationLabel !== 'Live') && (
                        <div style={{ color: activationColor, fontWeight: 700 }}>{isArchived ? 'Archived' : activationLabel}</div>
                      )}
                      <div style={{ marginTop: 2 }}>{isArchived ? formatDateTime(zone.archivedAt!) : zoneActivationDetail(zone)}</div>
                      {!isCatalogMode && !isArchived && !isZoneMetricFresh(metric) && (
                        <div style={{ marginTop: 3, color: isMock ? C.muted : zoneMetricFreshnessColor(metric, C), fontSize: 9 }}>
                          {isMock ? 'Simulation' : zoneMetricFreshnessText(metric)}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={event => event.stopPropagation()}>
                        {canRunOperationalActions && (
                          <Btn C={C} variant="ghost" size="sm" icon={<Icons.Map size={11} />} onClick={() => navigate(`/map?zoneId=${zone.id}`, { state: { focusZoneId: zone.id } })}>
                            Map
                          </Btn>
                        )}
                        {canArchive && (isCatalogMode || canRunOperationalActions) && !isArchived && zone.venueId && (
                          <Btn
                            C={C} variant="ghost" size="sm"
                            icon={<Icons.Trash size={11} />}
                            disabled={archiving === zone.id}
                            onClick={() => handleArchive(zone)}
                          >
                            Archive
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <PaginationControls
          C={C}
          pagination={zonePagination.pagination}
          loading={loading}
          error={null}
          emptyLabel={zones.length === 0 ? (tab === 'archived' ? 'No archived zones found.' : isCatalogMode ? 'No configured venue zones found. Create a zone from the catalog or a venue.' : 'No zones found. Create a zone to get started.') : 'No zones match the current filters.'}
          onPageChange={zonePagination.setPage}
          onPageSizeChange={zonePagination.setPageSize}
        />
      </Panel>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: C.green, color: '#fff', fontFamily: "'Albert Sans', sans-serif",
          fontSize: 13, fontWeight: 600, padding: '10px 20px', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 9999, pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── Zone detail view ──────────────────────────────────────────────────────────

function ZoneDetailView({ zone, C, mode, onBack, canArchive, onArchived }: {
  zone:       Zone;
  C:          ColorPalette;
  mode:       ZoneScreenMode;
  onBack:     () => void;
  canArchive: boolean;
  onArchived: () => void;
}) {
  const isCatalogMode = mode === 'catalog';
  const navigate = useNavigate();
  const { zoneTypeLabel, zoneTypeCode } = useCrowdTaxonomy();
  const { currentUser, selectedOperation, selectedOperationAlerts } = useApp();
  const recordActivity = useCrowdActivityAudit();
  const { teams } = useTeams();
  const { isMock } = useMockMode();
  const densityData = useDensityData();
  // Mirrors ZoneListView's gating so catalog-mode detail keeps the live REST metrics.
  const liveZoneMetrics = useLiveZoneMetrics(
    isCatalogMode ? null : selectedOperation?.id,
    Boolean(!isMock && (isCatalogMode || selectedOperation?.status === 'active')),
    2000,
  );
  const projectedSnapshots = useMemo(() => zoneMetricsDensitySnapshots(liveZoneMetrics.rows), [liveZoneMetrics.rows]);
  const densityHistoryWindow = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 60 * 60_000);
    return { fromIso: from.toISOString(), toIso: to.toISOString() };
  }, []);
  const densityHistory = useDensityHistory(
    isCatalogMode ? null : selectedOperation?.id,
    densityHistoryWindow.fromIso,
    densityHistoryWindow.toIso,
    Boolean(!isMock && !isCatalogMode && selectedOperation),
  );
  const snapshots = useMemo(
    () => isMock ? densityData.snapshots : { ...densityData.snapshots, ...projectedSnapshots },
    [densityData.snapshots, isMock, projectedSnapshots],
  );
  const [zones, setZones] = useState<Zone[]>(() => isMock ? MOCK_ZONES : [zone]);
  const [selectedZoneId, setSelectedZoneId] = useState(zone.id);
  const [detailTab, setDetailTab] = useState<ZoneDetailTab>('overview');
  const [activityFilter, setActivityFilter] = useState<ZoneEventFilter>('all');
  const [activityRange, setActivityRange] = useState<ZoneEventRange>('24h');
  const [rulesByZone, setRulesByZone] = useState<Record<string, ZoneAlertRule[]>>(() =>
    isMock ? { [zone.id]: buildMockRules(zone) } : {},
  );
  const [rulesLoadStatus, setRulesLoadStatus] = useState<'idle' | 'loading' | 'error'>(isMock ? 'idle' : 'loading');
  const [rulesReloadKey, setRulesReloadKey] = useState(0);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [draftThreshold, setDraftThreshold] = useState('');
  const [showRuleBuilder, setShowRuleBuilder] = useState(false);
  const [newRuleType, setNewRuleType] = useState<CrowdAlertType>('overcrowding');
  const [newRuleThreshold, setNewRuleThreshold] = useState(85);
  const [newRuleWindow, setNewRuleWindow] = useState(2);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (isCatalogMode) {
      if (isMock) {
        const catalogZones = MOCK_ZONES.filter(z => Boolean(z.venueId));
        setZones(catalogZones.some(z => z.id === zone.id) ? catalogZones : [zone]);
        return;
      }
      get<Zone[] | { data?: Zone[] }>(`/geo/zones?${crowdQuery({ archiveStatus: 'all' })}`)
        .then(body => {
          const catalogZones = (Array.isArray(body) ? body : (body.data ?? [])).filter(z => Boolean(z.venueId));
          setZones(catalogZones.some(z => z.id === zone.id) ? catalogZones : [zone]);
        })
        .catch(() => setZones([zone]));
      return;
    }

    if (isMock) {
      const scopedZones = selectedOperation
        ? MOCK_ZONES.filter(z => selectedOperation.assignedZoneIds.includes(z.id))
        : [];
      setZones(scopedZones.some(z => z.id === zone.id) ? scopedZones : [zone]);
      return;
    }

    if (!selectedOperation) {
      setZones([zone]);
      return;
    }

    get<Zone[] | { data?: Zone[] }>(`/operations/${selectedOperation.id}/zones?${crowdQuery({ archiveStatus: 'all' })}`)
      .then(body => {
        const operationZones = Array.isArray(body) ? body : (body.data ?? []);
        setZones(operationZones.some(z => z.id === zone.id) ? operationZones : [zone]);
      })
      .catch(() => setZones([zone]));
  }, [isCatalogMode, isMock, selectedOperation?.id, zone]);

  const currentZone = zones.find(z => z.id === selectedZoneId) ?? zone;
  const activeTeams = useMemo(() => activeCrowdTeams(teams), [teams]);
  const teamById = useMemo(() => buildTeamById(activeTeams), [activeTeams]);
  const currentZoneTeamName = isCatalogMode
    ? currentZone.assignedTeam
    : operationTeamNameForZone(selectedOperation, currentZone.id, teamById, currentZone.assignedTeam);
  const snap = snapshots[currentZone.id];
  const metric = liveZoneMetrics.byZone[currentZone.id];
  const util = Number(snap?.capacityUtilisation ?? 0);
  const currentZoneDensitySparkline = isMock
    ? (MOCK_HISTORY[currentZone.id] ?? []).slice(-12).map(pt => pt.capacityUtilisation)
    : (densityHistory.byZone[currentZone.id] ?? [])
      .slice()
      .sort((a, b) => new Date(a.sampledAt).getTime() - new Date(b.sampledAt).getTime())
      .map(pt => Number(pt.capacityUtilisation));
  // Status colour derives from real utilisation only, never faked from zone.status.
  const stColor = StatusColor(C, densityStatus(util));
  const activationColor = zoneActivationColor(currentZone, C);
  const storedRules = isMock
    ? (rulesByZone[currentZone.id] ?? buildMockRules(currentZone))
    : (rulesByZone[currentZone.id] ?? []);
  const rules = useMemo(
    () => rulesWithLiveTriggerTimes(storedRules, selectedOperationAlerts, currentZone.id),
    [currentZone.id, selectedOperationAlerts, storedRules],
  );
  const isInSelectedOperation = isCatalogMode || Boolean(selectedOperation?.assignedZoneIds.includes(currentZone.id));
  const isLegacyVenueZone = !currentZone.venueId;
  const canRunOperationalActions = allowsOperationalZoneActions(selectedOperation);
  const isReadOnlyContext = isCatalogMode || !isInSelectedOperation || isLegacyVenueZone || !canRunOperationalActions;
  const readOnlyReason = isCatalogMode
    ? 'Global zones view. Operational live controls are disabled outside selected operation context.'
    : isLegacyVenueZone
    ? 'This legacy zone has no venue linkage. Operational actions are disabled until it is backfilled or recreated under a venue.'
    : selectedOperation && !canRunOperationalActions
    ? `This operation is ${selectedOperation.status}. Zone actions are available only while an operation is active or paused.`
    : selectedOperation
    ? `This zone is not assigned to ${selectedOperation.name}. Operational actions are disabled.`
    : 'No operation is selected. This zone is shown read-only outside live operation context.';
  const events = isMock
    ? buildMockZoneEvents(currentZone, zoneTypeLabel)
    : buildRealZoneEvents(currentZone, selectedOperationAlerts, densityHistory.byZone[currentZone.id] ?? []);
  const nowMs = Date.now();
  const rangeMs = activityRange === '1h' ? 60 * 60_000 : activityRange === '6h' ? 6 * 60 * 60_000 : 24 * 60 * 60_000;
  const visibleEvents = events.filter(e => {
    const inType = activityFilter === 'all' || e.category === activityFilter;
    const inRange = activityRange === 'custom' || nowMs - new Date(e.timestamp).getTime() <= rangeMs;
    return inType && inRange;
  });

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    if (!isReadOnlyContext) return;
    setEditingRuleId(null);
    setShowRuleBuilder(false);
  }, [isReadOnlyContext]);

  function setRules(next: ZoneAlertRule[]) {
    setRulesByZone(prev => ({ ...prev, [currentZone.id]: next }));
  }

  useEffect(() => {
    if (isMock) {
      setRulesLoadStatus('idle');
      return;
    }
    let cancelled = false;
    setRulesLoadStatus('loading');
    get<SharedAlertRule[] | { data?: SharedAlertRule[] }>(`/alerts/rules?${crowdQuery({ zoneId: currentZone.id })}`)
      .then(body => {
        if (cancelled) return;
        const rows = Array.isArray(body) ? body : (body.data ?? []);
        const liveRules = rows
          .map(zoneAlertRuleFromPersisted)
          .filter((rule): rule is ZoneAlertRule => Boolean(rule));
        setRulesByZone(prev => ({ ...prev, [currentZone.id]: liveRules }));
        setRulesLoadStatus('idle');
      })
      .catch(() => {
        // A genuine fetch failure must surface as an error, not a silent empty/stale list.
        if (cancelled) return;
        setRulesLoadStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [currentZone.id, isMock, rulesReloadKey]);

  async function toggleRule(rule: ZoneAlertRule) {
    if (isReadOnlyContext) return;
    const nextRule = { ...rule, enabled: !rule.enabled };
    setRules(rules.map(r => r.id === rule.id ? nextRule : r));
    if (!isMock) await patch(`/alerts/rules/${rule.id}`, { enabled: nextRule.enabled });
    showToast(nextRule.enabled ? 'Alert rule enabled' : 'Alert rule disabled');
  }

  async function saveThreshold(rule: ZoneAlertRule) {
    if (isReadOnlyContext) return;
    const value = Number(draftThreshold);
    if (!Number.isFinite(value)) return;
    const nextRule = setPrimaryThreshold(rule, value);
    setRules(rules.map(r => r.id === rule.id ? nextRule : r));
    if (!isMock) await patch(`/alerts/rules/${rule.id}`, { thresholds: nextRule.thresholds });
    setEditingRuleId(null);
    showToast('Alert rule threshold updated');
  }

  async function addRule() {
    if (isReadOnlyContext) return;
    const nextRule: ZoneAlertRule = {
      id: `${currentZone.id}-new-${Date.now()}`,
      zoneId: currentZone.id,
      alertType: newRuleType,
      enabled: true,
      thresholds: newRuleType === 'crowd_surge'
        ? { surgeRatePct: newRuleThreshold, timeWindowMin: newRuleWindow }
        : { densityPct: newRuleThreshold, timeWindowMin: newRuleWindow },
      priority: newRuleThreshold >= 85 ? 'critical' : newRuleThreshold >= 70 ? 'high' : 'medium',
      lastTriggeredAt: null,
    };
    setRules([...rules, nextRule]);
    if (!isMock) await post('/alerts/rules', nextRule);
    setShowRuleBuilder(false);
    showToast('Alert rule added');
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'activity', label: 'Activity', count: visibleEvents.length },
    { id: 'triggers', label: 'Triggers & Rules', count: rules.length },
  ];

  function handleSelectedZoneChange(zoneId: string) {
    setSelectedZoneId(zoneId);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
      page: isCatalogMode ? '/admin/zones' : '/zones',
      operationId: isCatalogMode ? null : selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
      resourceId: zoneId,
      metadata: { sourcePage: isCatalogMode ? '/admin/zones' : '/zones' },
    });
  }

  function handleDetailTabChange(id: string) {
    setDetailTab(id as ZoneDetailTab);
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.tabChanged,
      page: isCatalogMode ? '/admin/zones' : '/zones',
      operationId: isCatalogMode ? null : selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.uiTab,
      resourceId: 'zone-detail',
      metadata: { tab: id, sourcePage: isCatalogMode ? '/admin/zones' : '/zones' },
    });
  }

  function handleViewOnMap() {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.drilldownOpened,
      page: isCatalogMode ? '/admin/zones' : '/zones',
      operationId: isCatalogMode ? null : selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
      resourceId: currentZone.id,
      metadata: { sourcePage: isCatalogMode ? '/admin/zones' : '/zones', targetPage: '/map' },
    });
    navigate(`/map?zoneId=${currentZone.id}`, { state: { focusZoneId: currentZone.id } });
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', fontFamily: "'Albert Sans', sans-serif" }}>
      <div style={{ padding: `${SPACE[16]}px ${SPACE[20]}px`, borderBottom: `1px solid ${C.border2}`, background: C.surface }}>
        <button onClick={onBack} style={{
          display: 'inline-flex', alignItems: 'center', gap: SPACE[5], marginBottom: SPACE[8],
          background: 'transparent', border: 'none', color: C.muted, fontSize: FONT_SIZE.base,
          cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
        }}>
          <Icons.ChevL size={SIZE.iconSm} /> Back to zones
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: SPACE[20] }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[10], marginBottom: SPACE[4], flexWrap: 'wrap' }}>
              <span style={{ fontSize: FONT_SIZE.display, fontWeight: FONT_WEIGHT.bold, color: C.text }}>{currentZone.name}</span>
              <Pill color={C.teal} hollow>{zoneTypeLabel(currentZone.type)}</Pill>
              <Pill color={stColor}>{densityStatus(util || 0)}</Pill>
              <Pill color={riskColor(currentZone.riskLevel, C)} hollow>{currentZone.riskLevel} risk</Pill>
            </div>
            <div style={{ fontSize: FONT_SIZE.base, color: C.muted, display: 'flex', gap: SPACE[12], flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace' }}>{shortId(currentZone.id)}</span>
              <span>{zoneCode(currentZone, zoneTypeCode)}</span>
              <span>{currentZoneTeamName ?? 'Unassigned'}</span>
              <span style={{ color: activationColor }}>Readiness {zoneActivationLabel(currentZone)}</span>
              <span>{zoneActivationDetail(currentZone)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: SPACE[6], alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <select
              value={selectedZoneId}
              onChange={e => handleSelectedZoneChange(e.target.value)}
              disabled={isReadOnlyContext}
              style={{ ...inputCss(C), width: 220, opacity: isReadOnlyContext ? 0.7 : 1 }}
            >
              {zones.map(z => <option key={z.id} value={z.id}>{zoneCode(z, zoneTypeCode)} - {z.name}</option>)}
            </select>
            <span title="Zone editing coming in Sprint 6">
              <Btn C={C} variant="ghost" icon={<Icons.Edit size={SIZE.iconSm} />} disabled>Edit</Btn>
            </span>
            {canArchive && !isReadOnlyContext && <Btn C={C} variant="ghost" icon={<Icons.Pause size={SIZE.iconSm} />} onClick={onArchived}>Deactivate</Btn>}
            {!isReadOnlyContext && <Btn C={C} variant="ghost" icon={<Icons.Map size={SIZE.iconSm} />} onClick={handleViewOnMap}>View on Map</Btn>}
          </div>
        </div>
      </div>

      {isReadOnlyContext && (
        <div style={{
          margin: `${SPACE[12]}px ${SPACE[20]}px 0`,
          padding: `${SPACE[12]}px ${SPACE[14]}px`,
          border: `1px solid ${C.border2}`,
          borderRadius: RADIUS.card,
          background: C.faint,
          display: 'flex',
          alignItems: 'center',
          gap: SPACE[10],
          color: C.muted,
          fontSize: FONT_SIZE.body,
          fontWeight: FONT_WEIGHT.semibold,
        }}>
          <Icons.Alert size={SIZE.iconSm} />
          <span>{readOnlyReason}</span>
        </div>
      )}

      <div style={{ padding: `0 ${SPACE[20]}px`, background: C.surface, borderBottom: `1px solid ${C.border2}` }}>
        <Tabs C={C} tabs={tabs} active={detailTab} onChange={handleDetailTabChange} />
      </div>

      <div style={{ padding: SPACE[20] }}>
        {detailTab === 'overview' && <ZoneOverviewTab C={C} zone={currentZone} assignedTeamName={currentZoneTeamName} snap={snap} metric={isMock || isCatalogMode ? null : metric} isSimulation={isMock} statusColor={stColor} densitySparkline={currentZoneDensitySparkline} />}
        {detailTab === 'activity' && (
          <ZoneActivityTab
            C={C}
            events={visibleEvents}
            filter={activityFilter}
            range={activityRange}
            onFilter={setActivityFilter}
            onRange={setActivityRange}
          />
        )}
        {detailTab === 'triggers' && (
          <ZoneTriggersTab
            C={C}
            rules={rules}
            loadStatus={rulesLoadStatus}
            onRetry={() => setRulesReloadKey(key => key + 1)}
            canAddRule={currentUser.role === 'system_admin' && !isReadOnlyContext}
            canEditRules={!isReadOnlyContext}
            editingRuleId={editingRuleId}
            draftThreshold={draftThreshold}
            onToggle={toggleRule}
            onEdit={rule => {
              setEditingRuleId(rule.id);
              setDraftThreshold(String(primaryThreshold(rule) ?? ''));
            }}
            onDraft={setDraftThreshold}
            onSave={saveThreshold}
            onCancel={() => setEditingRuleId(null)}
            showRuleBuilder={showRuleBuilder}
            newRuleType={newRuleType}
            newRuleThreshold={newRuleThreshold}
            newRuleWindow={newRuleWindow}
            onStartAdd={() => setShowRuleBuilder(true)}
            onCancelAdd={() => setShowRuleBuilder(false)}
            onNewRuleType={setNewRuleType}
            onNewRuleThreshold={setNewRuleThreshold}
            onNewRuleWindow={setNewRuleWindow}
            onAdd={addRule}
          />
        )}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: SPACE[24], left: '50%', transform: 'translateX(-50%)',
          background: C.green, color: '#fff', fontFamily: "'Albert Sans', sans-serif",
          fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.semibold,
          padding: `${SPACE[10]}px ${SPACE[20]}px`, borderRadius: RADIUS.lg,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 9999, pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}

function ZoneOverviewTab({ C, zone, assignedTeamName, snap, metric, isSimulation, statusColor, densitySparkline }: {
  C: ColorPalette; zone: Zone; assignedTeamName: string | null; snap?: DensitySnapshot; metric?: CurrentZoneMetrics | null; isSimulation: boolean; statusColor: string; densitySparkline: number[];
}) {
  const { zoneTypeLabel, zoneTypeCode } = useCrowdTaxonomy();
  const util = Number(snap?.capacityUtilisation ?? 0);
  const densityScore = Number(snap?.densityScore ?? util);
  const trend = snap?.trendDirection ?? 'stable';
  const trendRate = Number(snap?.trendRate ?? 0);
  const metricsFreshness = isSimulation ? 'Simulation' : zoneMetricFreshnessText(metric);
  const metricsFreshnessColor = isSimulation ? C.muted : zoneMetricFreshnessColor(metric, C);
  const hasSparkline = densitySparkline.length >= 2;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: SPACE[16] }}>
      <div>
        <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[12] }}>
          <KLabel C={C}>Capacity & Occupancy</KLabel>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: SPACE[10], marginBottom: SPACE[10] }}>
            <div style={{ fontSize: FONT_SIZE.hero, fontWeight: FONT_WEIGHT.black, color: statusColor, lineHeight: LINE_HEIGHT.tight }}>
              {snap ? snap.occupancyCount.toLocaleString() : '—'}
            </div>
            <div style={{ fontSize: FONT_SIZE.body, color: C.muted, paddingBottom: SPACE[4] }}>
              of {zone.capacity.toLocaleString()} total
            </div>
          </div>
          <div style={{ position: 'relative', height: SIZE.barLg, background: C.faint, borderRadius: RADIUS.sm, overflow: 'hidden', marginBottom: SPACE[8] }}>
            <div style={{ height: '100%', width: `${Math.min(util, 100)}%`, background: statusColor, transition: 'width 600ms ease' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '70%', width: SPACE[1], background: C.amber }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '85%', width: SPACE[1], background: C.ember }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: FONT_SIZE.base, color: C.muted, fontFamily: 'monospace', marginBottom: SPACE[12] }}>
            <span>Density score {formatUtilPct(densityScore, Boolean(snap))}</span>
            <span>Capacity utilisation {formatUtilPct(util, Boolean(snap))}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], color: trendColor(trend, C) }}>
            {trendIcon(trend)}
            <span style={{ fontSize: FONT_SIZE.body, fontWeight: FONT_WEIGHT.bold, textTransform: 'capitalize' }}>{trend}</span>
            <span style={{ fontSize: FONT_SIZE.base, color: C.muted, fontFamily: 'monospace' }}>{trendRate.toFixed(1)}%/min</span>
          </div>
        </Panel>

        <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[12] }}>
          <KLabel C={C}>Live Metrics</KLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: SPACE[14] }}>
            {[
              ['Occupancy count', snap ? snap.occupancyCount.toLocaleString() : '—', C.text],
              ['Density score', formatUtilPct(snap?.densityScore, Boolean(snap)), statusColor],
              ['Trend direction', trend, statusColor],
              ['Projection status', metricsFreshness, metricsFreshnessColor],
            ].map(([label, value, color]) => (
              <div key={label}>
                <div style={{ fontSize: FONT_SIZE.label, fontWeight: FONT_WEIGHT.bold, color: C.muted, letterSpacing: LETTER_SPACING.field, textTransform: 'uppercase', marginBottom: SPACE[4] }}>{label}</div>
                <div style={{ fontSize: FONT_SIZE.metric, fontWeight: FONT_WEIGHT.bold, color, lineHeight: LINE_HEIGHT.tight, textTransform: label === 'Trend direction' ? 'capitalize' : undefined }}>{value}</div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel C={C} style={{ padding: SPACE[18] }}>
          <KLabel C={C}>Density — Last 60 Minutes</KLabel>
          {hasSparkline ? (
            <>
              <svg viewBox="0 0 300 56" style={{ width: '100%', height: 56 }}>
                {[25, 50, 75].map(y => (
                  <line key={y} x1="0" y1={56 - (y / 100) * 52 - 2} x2="300" y2={56 - (y / 100) * 52 - 2}
                    stroke={C.border2} strokeDasharray="2,3" strokeWidth="0.5" />
                ))}
                <line x1="0" y1={56 - 0.85 * 52 - 2} x2="300" y2={56 - 0.85 * 52 - 2}
                  stroke={C.ember} strokeDasharray="3,2" strokeWidth="0.6" opacity="0.6" />
                <polyline
                  points={densitySparkline.map((v, i) => `${(i / Math.max(1, densitySparkline.length - 1)) * 300},${56 - (v / 100) * 52 - 2}`).join(' ')}
                  fill="none" stroke={statusColor} strokeWidth="1.5" strokeLinejoin="round"
                />
              </svg>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: FONT_SIZE.sidebarMark, color: C.muted, fontFamily: 'monospace', marginTop: SPACE[3] }}>
                <span>-60m</span><span>-30m</span><span>now</span>
              </div>
            </>
          ) : (
            <div style={{ color: C.muted, fontSize: FONT_SIZE.body, padding: `${SPACE[8]}px 0` }}>
              No density history available for this zone in the last 60 minutes.
            </div>
          )}
        </Panel>
      </div>

      <div>
        <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[12] }}>
          <KLabel C={C}>Zone Metadata</KLabel>
          {[
            ['Zone ID', shortId(zone.id), 'mono'],
            ['Type', zoneTypeLabel(zone.type), ''],
            ['Status', densityStatus(util), 'cap'],
            ['Risk level', zone.riskLevel, 'cap'],
          ].map(([label, value, mode]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: `${SPACE[6]}px 0`, borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>{label}</span>
              <span style={{
                fontSize: FONT_SIZE.basePlus, color: C.text, fontWeight: FONT_WEIGHT.semibold,
                fontFamily: mode === 'mono' ? 'monospace' : undefined,
                textTransform: mode === 'cap' ? 'capitalize' : undefined,
              }}>{value}</span>
            </div>
          ))}
        </Panel>

        <Panel C={C} style={{ padding: SPACE[18], marginBottom: SPACE[12] }}>
          <KLabel C={C}>Zone Geometry</KLabel>
          {zone.geometry ? <GeometryThumbnail geometry={zone.geometry} C={C} /> : null}
          <div style={{ fontSize: FONT_SIZE.micro, color: C.muted, marginTop: SPACE[6], fontFamily: 'monospace' }}>
            {zone.geometry?.type ?? 'Polygon'} · {zoneCode(zone, zoneTypeCode)}
          </div>
        </Panel>

        <Panel C={C} style={{ padding: SPACE[18] }}>
          <KLabel C={C}>Assignment</KLabel>
          {[
            ['Assigned team', assignedTeamName ?? '—'],
            ['Monitoring readiness', zoneActivationLabel(zone)],
            ['Activation window', zoneActivationDetail(zone)],
            ['Zone updated', formatRelTime(zone.updatedAt)],
            ['Metrics updated', isSimulation ? 'Simulation' : metric ? formatRelTime(metric.lastEventAt) : 'No projection'],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: `${SPACE[6]}px 0`, borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: FONT_SIZE.base, color: C.muted }}>{label}</span>
              <span style={{ fontSize: FONT_SIZE.basePlus, color: C.text, fontWeight: FONT_WEIGHT.semibold, textAlign: 'right' }}>{value}</span>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

function ZoneActivityTab({ C, events, filter, range, onFilter, onRange }: {
  C: ColorPalette;
  events: ZoneActivityEvent[];
  filter: ZoneEventFilter;
  range: ZoneEventRange;
  onFilter: (filter: ZoneEventFilter) => void;
  onRange: (range: ZoneEventRange) => void;
}) {
  const eventFilters: { id: ZoneEventFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'density', label: 'Density' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'zone_changes', label: 'Zone Changes' },
    { id: 'team_actions', label: 'Team Actions' },
  ];
  const ranges: { id: ZoneEventRange; label: string }[] = [
    { id: '1h', label: 'Last 1h' },
    { id: '6h', label: 'Last 6h' },
    { id: '24h', label: 'Last 24h' },
    { id: 'custom', label: 'Custom' },
  ];

  const eventColor = (event: ZoneActivityEvent) =>
    event.colorKey === 'teal' ? C.teal
    : event.colorKey === 'normal' ? C.green
    : event.colorKey === 'warning' ? C.amber
    : event.colorKey === 'muted' ? C.muted
    : SeverityColor(C, event.colorKey);

  return (
    <Panel C={C}>
      <div style={{ padding: `${SPACE[12]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`, display: 'flex', justifyContent: 'space-between', gap: SPACE[12], alignItems: 'center', flexWrap: 'wrap' }}>
        <KLabel C={C} mb={0}>Chronological Event Log</KLabel>
        <div style={{ display: 'flex', gap: SPACE[6], flexWrap: 'wrap' }}>
          {eventFilters.map(item => (
            <button key={item.id} onClick={() => onFilter(item.id)} style={{
              fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, padding: `${SPACE[4]}px ${SPACE[8]}px`, borderRadius: RADIUS.sm,
              background: filter === item.id ? C.amberDim : 'transparent',
              border: `1px solid ${filter === item.id ? `${C.amber}55` : C.border2}`,
              color: filter === item.id ? C.amber : C.muted,
              cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
            }}>{item.label}</button>
          ))}
          <select value={range} onChange={e => onRange(e.target.value as ZoneEventRange)} style={{ ...inputCss(C), width: 120, padding: `${SPACE[4]}px ${SPACE[8]}px`, fontSize: FONT_SIZE.xs }}>
            {ranges.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </div>
      </div>
      {events.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: `${SPACE[32]}px 0` }}>
          <Icons.Clock size={SIZE.iconXl} />
          <div style={{ fontSize: FONT_SIZE.body, marginTop: SPACE[10], fontWeight: FONT_WEIGHT.semibold }}>No events in this time range</div>
        </div>
      ) : events.map(event => {
        const EventIcon = Icons[event.icon] ?? Icons.Activity;
        const color = eventColor(event);
        return (
          <div key={event.id} style={{ padding: `${SPACE[11]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`, display: 'flex', gap: SPACE[14], alignItems: 'flex-start' }}>
            <span style={{ width: SIZE.dot, height: SIZE.dot, borderRadius: '50%', background: color, marginTop: SPACE[6], flexShrink: 0 }} />
            <span style={{ color, flexShrink: 0, marginTop: SPACE[2] }}><EventIcon size={SIZE.iconSm} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: FONT_SIZE.body, color: C.text }}>{event.description}</div>
              {event.detail && <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[2] }}>{event.detail}</div>}
            </div>
            <span style={{ fontSize: FONT_SIZE.micro, color: C.muted, flexShrink: 0, fontFamily: 'monospace' }}>{formatRelTime(event.timestamp)}</span>
          </div>
        );
      })}
    </Panel>
  );
}

function ZoneTriggersTab({
  C, rules, loadStatus, onRetry, canAddRule, canEditRules, editingRuleId, draftThreshold,
  showRuleBuilder, newRuleType, newRuleThreshold, newRuleWindow,
  onToggle, onEdit, onDraft, onSave, onCancel,
  onStartAdd, onCancelAdd, onNewRuleType, onNewRuleThreshold, onNewRuleWindow, onAdd,
}: {
  C: ColorPalette;
  rules: ZoneAlertRule[];
  loadStatus: 'idle' | 'loading' | 'error';
  onRetry: () => void;
  canAddRule: boolean;
  canEditRules: boolean;
  editingRuleId: string | null;
  draftThreshold: string;
  showRuleBuilder: boolean;
  newRuleType: CrowdAlertType;
  newRuleThreshold: number;
  newRuleWindow: number;
  onToggle: (rule: ZoneAlertRule) => void;
  onEdit: (rule: ZoneAlertRule) => void;
  onDraft: (value: string) => void;
  onSave: (rule: ZoneAlertRule) => void;
  onCancel: () => void;
  onStartAdd: () => void;
  onCancelAdd: () => void;
  onNewRuleType: (type: CrowdAlertType) => void;
  onNewRuleThreshold: (value: number) => void;
  onNewRuleWindow: (value: number) => void;
  onAdd: () => void;
}) {
  const builderTypes: { type: CrowdAlertType; label: string; icon: keyof typeof Icons }[] = [
    { type: 'overcrowding', label: 'Overcrowding', icon: 'Alert' },
    { type: 'zone_capacity_warning', label: 'Capacity Warning', icon: 'Alert' },
    { type: 'crowd_surge', label: 'Crowd Surge', icon: 'Zap' },
    { type: 'threshold_breach', label: 'Threshold Breach', icon: 'Activity' },
  ];

  return (
    <Panel C={C}>
      <div style={{ padding: `${SPACE[12]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <KLabel C={C} mb={0}>Alert Rules ({rules.length})</KLabel>
        {canAddRule && <Btn C={C} variant="ghost" size="sm" icon={<Icons.Plus size={SIZE.iconXs} />} onClick={onStartAdd}>Add Alert Rule</Btn>}
      </div>
      {showRuleBuilder && (
        <div style={{ padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border2}`, background: C.surface }}>
          <div style={{ fontSize: FONT_SIZE.stepTitle, fontWeight: FONT_WEIGHT.bold, color: C.text, marginBottom: SPACE[4] }}>Configure alert threshold</div>
          <div style={{ fontSize: FONT_SIZE.body, color: C.muted, marginBottom: SPACE[14] }}>
            Create a basic threshold rule using the same condition model as the zone wizard.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE[10] }}>
            {builderTypes.map(item => {
              const Ico = Icons[item.icon];
              const selected = newRuleType === item.type;
              return (
                <button key={item.type} onClick={() => onNewRuleType(item.type)} style={{
                  display: 'flex', alignItems: 'center', gap: SPACE[12], textAlign: 'left',
                  background: selected ? C.amberDim : C.panel,
                  border: `1px solid ${selected ? `${C.amber}55` : C.border2}`,
                  borderRadius: RADIUS.lg, padding: SPACE[12],
                  cursor: 'pointer', fontFamily: "'Albert Sans', sans-serif",
                }}>
                  <span style={{ width: SIZE.avatarMd, height: SIZE.avatarMd, borderRadius: RADIUS.card, background: selected ? C.amberDim : C.faint, color: selected ? C.amber : C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ico size={SIZE.iconMd} />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.bold, color: C.text }}>{item.label}</div>
                    <div style={{ fontSize: FONT_SIZE.sm, color: C.muted }}>{item.type === 'crowd_surge' ? 'Detect rapid density increase within a time window.' : 'Trigger when occupancy exceeds a configured threshold.'}</div>
                  </div>
                  {selected && <Icons.Check size={SIZE.iconMd} />}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: SPACE[10], marginTop: SPACE[14], alignItems: 'center', flexWrap: 'wrap' }}>
            <ThresholdInput
              label={newRuleType === 'crowd_surge' ? 'Increase >' : 'Density >'}
              value={newRuleThreshold}
              unit="%"
              onChange={onNewRuleThreshold}
              min={1}
              max={100}
              C={C}
            />
            <ThresholdInput
              label="For ≥"
              value={newRuleWindow}
              unit="min"
              onChange={onNewRuleWindow}
              min={1}
              max={60}
              C={C}
            />
            <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACE[8] }}>
              <Btn C={C} variant="ghost" size="sm" icon={<Icons.X size={SIZE.iconSm} />} onClick={onCancelAdd}>Cancel</Btn>
              <Btn C={C} variant="primary" size="sm" icon={<Icons.Check size={SIZE.iconSm} />} onClick={onAdd}>Save Rule</Btn>
            </div>
          </div>
        </div>
      )}
      {loadStatus === 'error' ? (
        <div style={{ textAlign: 'center', color: C.ember, padding: `${SPACE[32]}px 0` }}>
          <Icons.Alert size={SIZE.iconXl} />
          <div style={{ fontSize: FONT_SIZE.body, marginTop: SPACE[10], fontWeight: FONT_WEIGHT.semibold }}>Failed to load alert rules</div>
          <div style={{ fontSize: FONT_SIZE.sm, color: C.muted, marginTop: SPACE[4] }}>The rule list could not be fetched from the server.</div>
          <div style={{ marginTop: SPACE[12] }}><Btn C={C} variant="ghost" icon={<Icons.Refresh size={SIZE.iconSm} />} onClick={onRetry}>Retry</Btn></div>
        </div>
      ) : loadStatus === 'loading' ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: `${SPACE[32]}px 0`, fontSize: FONT_SIZE.body }}>
          Loading alert rules…
        </div>
      ) : rules.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.muted, padding: `${SPACE[32]}px 0` }}>
          <Icons.Alert size={SIZE.iconXl} />
          <div style={{ fontSize: FONT_SIZE.body, marginTop: SPACE[10], fontWeight: FONT_WEIGHT.semibold }}>No alert rules configured for this zone</div>
          {canAddRule && <div style={{ marginTop: SPACE[12] }}><Btn C={C} variant="ghost" icon={<Icons.Plus size={SIZE.iconSm} />} onClick={onStartAdd}>Add Alert Rule</Btn></div>}
        </div>
      ) : rules.map(rule => {
        const threshold = primaryThreshold(rule);
        const severityColor = SeverityColor(C, rule.priority);
        const isTokenRule = rule.alertType === 'flagged_token_entry' || rule.alertType === 'flagged_token_reentry';
        return (
          <div key={rule.id} style={{ padding: `${SPACE[14]}px ${SPACE[18]}px`, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: SPACE[16] }}>
            <Toggle enabled={rule.enabled} onClick={() => onToggle(rule)} C={C} disabled={!canEditRules} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACE[8], marginBottom: SPACE[2], flexWrap: 'wrap' }}>
                <span style={{ fontSize: FONT_SIZE.sub, fontWeight: FONT_WEIGHT.semibold, color: C.text }}>{formatRuleType(rule.alertType)}</span>
                <Badge C={C} color={severityColor} bg={`${severityColor}22`} border={`${severityColor}55`} size={FONT_SIZE.xs}>{rule.priority}</Badge>
                {rule.autoEscalateAfterMin != null && (
                  <Badge C={C} color={C.amber} bg={C.amberDim} border={`${C.amber}55`} size={FONT_SIZE.xs}>
                    Auto-escalates after {rule.autoEscalateAfterMin}m
                  </Badge>
                )}
              </div>
              <div style={{ fontSize: FONT_SIZE.body, color: C.muted, fontStyle: isTokenRule ? 'italic' : undefined }}>
                {thresholdDescription(rule)}
              </div>
              <div style={{ fontSize: FONT_SIZE.xs, color: C.muted, marginTop: SPACE[4] }}>
                Last triggered: {rule.lastTriggeredAt ? formatRelTime(rule.lastTriggeredAt) : 'Never triggered'}
              </div>
            </div>
            {threshold != null && (
              <div style={{ minWidth: 120, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: SPACE[6] }}>
                {canEditRules && editingRuleId === rule.id ? (
                  <>
                    <input type="number" value={draftThreshold} onChange={e => onDraft(e.target.value)} style={{ ...inputCss(C), width: 70, fontFamily: 'monospace' }} />
                    <Btn C={C} variant="ghost" size="sm" icon={<Icons.Check size={SIZE.iconXs} />} onClick={() => onSave(rule)} />
                    <Btn C={C} variant="ghost" size="sm" icon={<Icons.X size={SIZE.iconXs} />} onClick={onCancel} />
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.amber, fontFamily: 'monospace' }}>
                      {threshold}{rule.thresholds.personCount != null ? '' : '%'}
                    </span>
                    {canEditRules && <Btn C={C} variant="ghost" size="sm" icon={<Icons.Edit size={SIZE.iconXs} />} onClick={() => onEdit(rule)} />}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </Panel>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

export function ZonesScreen({ mode = 'operational' }: { mode?: ZoneScreenMode }) {
  const { theme, currentUser, selectedOperation } = useApp();
  const C = getCrowdC(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const recordActivity = useCrowdActivityAudit();

  // Auto-open wizard when navigating from venue/operation context.
  const createForVenue = searchParams.get('createForVenue');
  const createForOperation = searchParams.get('createForOperation');

  const [view, setView] = useState<'list' | 'wizard' | 'detail'>(() =>
    createForVenue || createForOperation ? 'wizard' : 'list'
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedZoneDetail, setSelectedZoneDetail] = useState<Zone | null>(null);

  const isAdmin = currentUser.role === 'system_admin';
  const canCreateZone = currentUser.role === 'command_supervisor' || isAdmin;
  const isCatalogMode = mode === 'catalog';
  const wizardOperationId = createForOperation ?? (!isCatalogMode && view === 'wizard' ? selectedOperation?.id ?? null : null);

  const openZoneDetail = useCallback((zone: Zone) => {
    recordActivity({
      action: CROWD_CLIENT_ACTIVITY_ACTION.recordViewed,
      page: isCatalogMode ? '/admin/zones' : '/zones',
      operationId: isCatalogMode ? null : selectedOperation?.id,
      resourceType: CROWD_CLIENT_RESOURCE_TYPE.zone,
      resourceId: zone.id,
      metadata: { sourcePage: isCatalogMode ? '/admin/zones' : '/zones' },
    });
    setSelectedZoneDetail(zone);
    setView('detail');
  }, [isCatalogMode, recordActivity, selectedOperation?.id]);

  // Auto-open zone detail when navigated from Venues screen with a zone in state.
  useEffect(() => {
    const fromState = (location.state as { openZone?: Zone } | null)?.openZone;
    if (fromState) {
      openZoneDetail(fromState);
      navigate(isCatalogMode ? '/admin/zones' : '/zones', { replace: true, state: {} });
    }
  }, [isCatalogMode, location.state, navigate, openZoneDetail]);

  useEffect(() => {
    if (createForVenue || createForOperation) {
      setSelectedZoneDetail(null);
      setView('wizard');
    }
  }, [createForVenue, createForOperation]);

  function handleWizardSuccess() {
    setView('list');
    setSearchParams({});
    setRefreshKey(k => k + 1);
  }

  if (isCatalogMode && !isAdmin) {
    return (
      <div style={{ flex: 1, overflow: 'auto', padding: 20, fontFamily: "'Albert Sans', sans-serif" }}>
        <Panel C={C} style={{ padding: SPACE[32], textAlign: 'center' }}>
          <Icons.Zones size={SIZE.iconXl} />
          <div style={{ fontSize: FONT_SIZE.subheading, fontWeight: FONT_WEIGHT.bold, color: C.text, marginTop: SPACE[12] }}>
            Zone catalog requires Admin access
          </div>
          <div style={{ maxWidth: 520, margin: `${SPACE[6]}px auto 0`, fontSize: FONT_SIZE.body, color: C.muted, lineHeight: LINE_HEIGHT.relaxed }}>
            Operational users should use the selected-operation Zones view. Global zone configuration is available to System Administrators.
          </div>
        </Panel>
      </div>
    );
  }

  if (view === 'wizard') {
    return (
      <ZoneCreationWizard
        C={C}
        createForVenueId={createForVenue}
        createForOperationId={wizardOperationId}
        onClose={() => { setSearchParams({}); setView('list'); }}
        onSuccess={handleWizardSuccess}
      />
    );
  }

  if (view === 'detail' && selectedZoneDetail) {
    return (
      <ZoneDetailView
        zone={selectedZoneDetail}
        C={C}
        mode={mode}
        onBack={() => setView('list')}
        canArchive={isAdmin}
        onArchived={() => { setView('list'); setRefreshKey(k => k + 1); }}
      />
    );
  }

  return (
    <ZoneListView
      key={refreshKey}
      C={C}
      mode={mode}
      onCreateZone={() => {
        if (!isCatalogMode && selectedOperation) {
          setSearchParams({ createForOperation: selectedOperation.id });
        } else {
          setSearchParams({});
        }
        setView('wizard');
      }}
      canCreate={canCreateZone}
      canArchive={isAdmin}
      onZoneClick={openZoneDetail}
    />
  );
}
